/**
 * Root application component — the workspace "shell".
 *
 * Holds a real contract `UserWorkspace` in mutable React state (useReducer over
 * the pure actions in workspace-actions.ts), and renders its active `Layout` as
 * a resizable section grid (SectionGrid). The single `chartGroup` section is a
 * live SciChart chart (ChartGroupView); other section kinds are typed
 * placeholders. A toolbar drives symbol / frequency / chart-type mutations.
 *
 * The active theme is `workspace.themeRef` (D13) resolved by `ThemeProvider`,
 * which publishes it as CSS variables for chrome and as context for the two
 * renderers that need real values. Everything below writes `var(--qf-*)` — no
 * component here holds a color.
 *
 * The QueVee chat (`components/chat/ChatPanel.tsx`) is a dock panel that calls
 * the very same workspace actions, through the action registry.
 *
 * TODO (later): React Router, Firebase Auth, live transport (WS + Arrow).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  mintUlid,
  type ChartGroup,
  type Dock,
  type Execution,
  type Plan,
  type Trade,
  type Frequency,
  type Layout,
  type ScreenerConfig,
  type SavedScreenerFilter,
  type Theme,
  type UserWorkspace,
} from "@qufolio/model";
import {
  applySelection,
  moveCategory,
  SavedFilterPicker,
  presetCopy,
  ScreenBuilder,
  buildUniverse as buildColumnUniverse,
  type BuilderResult,
  selectedFilterIds,
  ScreenerBuilder,
  summarizeFilter,
  toggleFavorite,
  libraryForAnotherScreen,
  stripLinks,
  type CopyFromScreen,
  type DslCatalog,
} from "@qufolio/screener-grid";
import { groupDisplayName } from "./lib/group-layouts";
import { publishMarks } from "./lib/screener-marks";
import {
  builderDescribeEnabled,
  compileExpr,
  describeEnabled,
  describeExpr,
  describeInBuilder,
  fetchDslCatalog,
} from "./api/compile-expr.js";
import {
  deleteLayoutTemplate,
  fetchLayout,
  fetchLayoutOverrides,
  layoutsConfigured,
  saveLayoutOverride,
  saveLayoutTemplate,
} from "./api/layouts";
import { useGroupLayoutSync } from "./api/group-layout-sync";
import { LegacyLayoutDialog } from "./components/LegacyLayoutDialog";
import { SharedUpdateDialog } from "./components/SharedUpdateDialog";
import { publishedDay, sharedState, type SharedState } from "./lib/group-layouts";
import type { GroupAccount } from "./api/me";
import { fetchPreset } from "./api/presets";
import { publishFromEditor, screenLoader } from "./lib/screen-draft";
import { isYManual, requestYAxisMode } from "./lib/y-axis-mode";
import { linksOffered, useScreenLinks } from "./lib/screen-links";
import { chartTemplatesConfigured } from "./api/chart-templates";
import { ApplyChartTemplate, SaveChartTemplate } from "./components/ChartTemplates";
import { ScreenList } from "./components/ScreenList";
import { TopDownEditor } from "./components/TopDownEditor";
import { newTopDown } from "./lib/topdown";
import { newSeasonality, tierOf } from "./lib/seasonality-screen";
import { useEntitlements } from "./lib/entitlements";
import { ScreenTabs } from "./components/ScreenTabs";
import {
  addScreen,
  defaultScreen,
  nextFreeName,
  loadScreens,
  removeScreen,
  renameScreen,
  isAuthored,
  sealedByName,
  screensSnapshot,
  useScreens,
} from "./lib/screens";
import { applyScreenTo } from "./lib/screen-sync";
import { runSimulation } from "./api/sim";
import { adoptLayout } from "./lib/layout-adopt";
import { PublishedScreens } from "./components/PublishedScreens";
import { fetchMe } from "./api/me";
import { startActivity } from "./api/activity";
import { claimVisits } from "./api/visits";
import { lastAccepted, recordAccept } from "./api/disclaimer";
import { DisclaimerGate } from "./components/DisclaimerGate";
import { BRAND } from "./lib/brand-current";
import {
  disclaimerFor,
  rememberAccept,
  rememberedAccept,
  stillAccepted,
  type AcceptMethod,
  type Disclaimer,
} from "./lib/disclaimer";
import { loadColumnsSplit, saveColumnsSplit } from "./lib/editor-split.js";
import { emptyCalendar, fetchMarketCalendar, type MarketCalendar } from "./api/calendar";
import { usePersistentWorkspace, type SaveStatus } from "./api/persistence";
import { type WorkspaceAction } from "./lib/workspace-actions";
import { activeChartSection, chartSections } from "./lib/active-section";
import { chartCycleOrder, cycleChart } from "./lib/fullscreen-cycle";
import { findChartGroup, isSymbolLocked, routePick, routeTyped } from "./lib/symbol-lock";
import { FinStGrainContext, type FinStGrainStore } from "./components/fin-st-grain";
import { moveTab as moveTabOrder } from "./lib/tab-strip";
import {
  restoreColumnSizes,
  onlySection,
  restoreRowSizes,
  toolbarAnchor,
  withoutScreener,
} from "./lib/layout-view";
import { addScreener, screenerPlacement, type ScreenerPlacement } from "./lib/layout-templates";
import {
  nearestShown,
  selectionAfterHide,
  selectionAfterRemove,
} from "./lib/layout-visibility";
import { DEFAULT_COLUMNS, newScreenerSection, screenerSeed } from "./lib/screener-section";
import { SectionGrid, type ScreenerSchema } from "./components/SectionGrid";
import type { ChartJump } from "./components/macro/types";
import { chartLayoutTargets } from "./lib/macro-calendar";
import { LayoutBar } from "./components/LayoutBar";
import { InheritedLayouts } from "./components/InheritedLayouts";
import { ChartToolbar, ChartTypeButton } from "./components/ChartToolbar";
import { PlanForm } from "./components/PlanForm";
import { PlanScreen } from "./components/PlanScreen";
import { GroupRequestForm } from "./components/GroupRequestForm";
import { readBillingReturn, takePlanIntent, withoutBillingReturn } from "./lib/billing";
import { DraggableDialog } from "./components/DraggableDialog";
import { AdminPanel } from "./components/AdminPanel";
import { BooksPanel } from "./components/BooksPanel";
import { TrainDialog } from "./components/TrainDialog";
import { fetchVoices, type Voice } from "./api/voice";
import { DataHealthPanel } from "./components/DataHealthPanel";
import { withdrawPublished } from "./api/versions";
import { PlanPanel } from "./components/PlanPanel";
import { TradesView } from "./components/TradesView";
import type { TradeGroup } from "./lib/trade-groups";
import {
  journalAnnotations as journalAnnotationsFor,
  parseLevelId,
} from "./lib/plan-annotations";
import { withLevel } from "./lib/plan-levels";
import { canRemoveTarget, nextTargetPrice } from "./lib/plan-math";
import { AnnotationTools, type AnnotationTool } from "./components/AnnotationTools";
import {
  amendPlan,
  fetchAccount,
  fetchJournal,
  journalEnabled,
  savePlan,
} from "./api/journal";
import {
  PlanIcon,
  DataPanelIcon,
  EditScreenerIcon,
  ConditionIcon,
  GridToolbarButton,
  ShowScreenerIcon,
  SplitIcon,
  CommunityIcon,
} from "./components/GridToolbarButton";
import { DockEmpty } from "./components/DockCell";
import { CommunityPanel } from "./components/community/CommunityPanel";
import { communityEnabled } from "./api/community";
import { ChatPanel } from "./components/chat/ChatPanel";
import { QueVeeMark } from "./components/QueVeeMark";
import { chatConfigured } from "./api/chat";
import { askAiOpener, chatGate, workspaceContext } from "./lib/chat";
import {
  addPanel,
  closePanel,
  dockDirection,
  dockOf,
  dockSizes,
  isShowing,
  isSplit,
  openPanel,
  swapPanels,
  toggleSplit,
  togglePanel,
  isPinned,
  togglePin,
} from "./lib/dock";
import { IndicatorButton } from "./components/IndicatorPicker";
import { setCandleDefaultFromGroups } from "./lib/candle-style";
import { appliedIndicators, removeIndicator } from "./lib/add-indicator";
import { cachedCatalog } from "./api/indicators";
import type { LegendControls } from "./components/ValueLegend";
import { chartGroupConfig, dataViewConfig, screenerConfig } from "./lib/section-config";
import {
  chartTypeChoice,
  pricePlot,
  volumeOverlaid,
  type ChartTypeChoice,
} from "./lib/chart-group";
import {
  pipDrawnOn,
  pipFrequencyFor,
  pipOfferedOn,
  swapWithChart,
  type PipPatch,
} from "./lib/pip";
import { visibleBarsOf } from "./lib/chart-window";
import { ThemeProvider, useTokenResolver } from "./components/theme";
import { available } from "./lib/features";
import { ContextMenu } from "./components/ContextMenu";
import { CommandSearch } from "./components/CommandSearch";
import {
  DockedDataPanel,
  HoveredDataPanel,
  SymbolDataPanel,
  SymbolDataTabs,
  SymbolDataView,
} from "./components/SymbolDataPanel";
import { ViewDetachContext, type ViewDetach } from "./components/view-detach";
import { SYMBOL_VIEWS, symbolView } from "./components/symbol-views";
import { useHoverIntent } from "./lib/use-hover-intent";
import { ACTIONS, actionById, clientTools, searchActions, type ActionContext } from "./lib/actions";
import { guideIndex, loadGuideIndex } from "./api/guide-index";
import { openGuideAt } from "./lib/guide";
import { setActionRunner } from "./lib/action-runner";
import { chartTable } from "./lib/chart-export";
import { screenerCommands } from "./lib/screener-commands";
import { trackListsSnapshot } from "./lib/track-lists";
import { trackListsEnabled } from "./api/tracklists";
import type { DataTab } from "./lib/pip";
import { DetachedWindow } from "./components/DetachedWindow";
import { dataPanelTarget } from "./lib/data-panel-target";
import {
  canDetach,
  DATA_WINDOW_NAME,
  isStandalone,
  openDataWindow,
  viewsInWindows,
  viewWindowName,
} from "./lib/detach";
import { desktopBridge } from "./lib/desktop";
import {
  FOLLOWER,
  ROLE,
  announceFocus,
  useFocusByLayout,
  useLastFocus,
  useLayoutWindows,
  type RemoteFocus,
} from "./lib/use-layout-windows";
import { DataViewCell } from "./components/DataViewCell";
import { followOf, isDataOnly, keysInUse, setCell, setFollow } from "./lib/data-layout";
import { newChartSection } from "./lib/chart-group";
import { layoutsInWindows } from "./lib/window-sync";
import { isComposite } from "./lib/composite";
import { symbolIndex, symbolLabel, useSymbolName } from "./lib/symbol-index";
import { symbolsConfigured } from "./api/symbols";
import { chatOpener, setChatOpener } from "./lib/chat-open";
import type { SymbolRoute } from "./lib/symbol-lock";
import { SymbolDialog } from "./components/SymbolDialog";
import { useTypeToSearch } from "./lib/use-type-to-search";
import { ThemeEditor } from "./components/ThemeEditor";
import { SignIn } from "./components/SignIn";
import { ActingAsBar } from "./components/ActingAs";
import { UpdateBar } from "./components/UpdateBar";
import { PauseBar } from "./components/PauseBar";
import { MobileApp } from "./components/MobileApp";
import { useIsNarrow } from "./lib/use-narrow";
import { Tour } from "./components/Tour";
import { resetTourSeen, shouldAutoStart } from "./lib/tour";
import { strategyColumns, strategyOnScreen, subscribeStrategyOnScreen } from "./lib/strategy";

/**
 * Anchors the saved-filter panel beside its toolbar button, **over the charts**.
 *
 * The strip stands between the charts and the screener, and the panel is
 * filtering the screener — so it opens onto the chart side, whichever side that
 * is. With the screener docked left it therefore opens right, the mirror of the
 * original rule rather than an exception to it.
 */
const filterPopoverAnchor = (side: "left" | "right"): React.CSSProperties => ({
  position: "absolute",
  top: 0,
  ...(side === "right" ? { right: "calc(100% + 6px)" } : { left: "calc(100% + 6px)" }),
  zIndex: 40,
});

/** The pinned conditions panel: the popover's own width, so pinning does not reflow it. */
const CONDITIONS_PINNED_WIDTH = 260;

/** How long "DIA is locked · 🔓 Unlock" stays under the symbol box, in ms. */
const LOCK_NOTICE_MS = 4000;

/**
 * The gridline patterns offered in the menu.
 *
 * A closed list rather than a dash-array editor: "dotted" is the choice a person
 * wants to make, and `[1, 3]` is only how it gets drawn. The engine still honours
 * a per-axis dash for anything this cannot say.
 */
const GRID_LINE_CHOICES = [
  { id: "solid", label: "Solid" },
  { id: "dashed", label: "Dashed" },
  { id: "dotted", label: "Dotted" },
  { id: "none", label: "Off" },
] as const;

/**
 * Which direction the wheel zooms and a drag pans.
 *
 * Three named choices for the same reason the gridlines are four: "zoom time
 * only" is the decision, and `EXyDirection.XDirection` is one renderer's
 * spelling of it. `Both` is last because it is the one that fights an
 * auto-ranged price axis, so it should not read as the obvious pick.
 */
const ZOOM_AXIS_CHOICES = [
  { id: "x", label: "Time" },
  { id: "y", label: "Price" },
  { id: "both", label: "Both" },
] as const;

/**
 * How much empty space to keep to the right of the last bar.
 *
 * Percentages of the window rather than a bar count, because the point is how
 * much of the *screen* stays clear: "twenty bars" is a third of a zoomed-in
 * chart and invisible on a zoomed-out one, while a tenth is a tenth at every
 * zoom. Four steps rather than a slider, for the same reason the gridlines are
 * four named patterns — nobody wants to tune 0.07.
 */
/**
 * What the price axis measures itself against.
 *
 * Two choices, named for what the scale is *about* rather than for the data kind
 * behind it. `All plots` is what an auto-ranged axis has always done; `Price
 * only` reads the bars' own highs and lows and lets an overlay fall where it
 * falls — which is the point, because an average or a band that runs far from
 * the bars forces the axis to cover it and squeezes the candles into a strip.
 *
 * Only ever affects a pane that has a price on it: `seriesForFit` falls back to
 * every series where none is OHLC-fed, so RSI, MACD and volume are untouched.
 */
const PRICE_FIT_CHOICES = [
  { id: "price", label: "Price only" },
  { id: "all", label: "All plots" },
] as const;

const RIGHT_MARGIN_CHOICES = [
  { id: 0, label: "Off" },
  { id: 0.05, label: "5%" },
  { id: 0.1, label: "10%" },
  { id: 0.2, label: "20%" },
] as const;

export function App() {
  return (
    <SignIn>
      <AuthenticatedApp />
    </SignIn>
  );
}

/**
 * Everything behind the gate.
 *
 * Split out so `usePersistentWorkspace` runs **after** identity is known.
 * Called above the gate it would load the fallback user's workspace on mount
 * and then discover the real one — two loads, and a visible flash of somebody
 * else's layout.
 */
function AuthenticatedApp() {
  // A layout window (`?layout=`) follows the main window's workspace instead of
  // loading and saving its own — see `lib/window-sync.ts`.
  const { workspace, dispatch, status, themes, saveTheme } = usePersistentWorkspace(FOLLOWER);
  const layoutWindows = useLayoutWindows(workspace, status, dispatch);
  const soleLayoutId = ROLE.kind === "layout" ? ROLE.layoutId : undefined;
  /**
   * Which shell. **Below the workspace, deliberately.**
   *
   * Forked above `usePersistentWorkspace`, the two shells would each own a copy
   * of the hook, so rotating a tablet across the breakpoint would unmount one
   * and load the workspace again in the other. Below it, the swap is a
   * re-render of state that is already in memory.
   *
   * The desktop shell is untouched by any of this: it is the same element with
   * the same props it has always had.
   */
  const narrow = useIsNarrow();
  /**
   * Which layout is on screen — held here, above the fork, so crossing the
   * breakpoint keeps it. Each shell held its own and both started at 0, so a
   * resize (or a rotate) dropped you back on the first layout.
   */
  const [chosenIndex, setLayoutIndex] = useState(0);
  // A layout window shows its one layout, wherever the main window's edits
  // move it in the list.
  const soleIndex = soleLayoutId ? workspace.layouts.findIndex((l) => l.id === soleLayoutId) : -1;
  const layoutIndex = soleLayoutId ? Math.max(0, soleIndex) : chosenIndex;

  // Its layout deleted in the main window, a layout window has nothing to show.
  useEffect(() => {
    if (soleLayoutId && status !== "loading" && soleIndex < 0) window.close();
  }, [soleLayoutId, status, soleIndex]);


  /**
   * Open a layout in its own window, and move this window off it: one layout is
   * on one screen at a time, as a data panel view is.
   */
  const detachLayout = useCallback(
    (layoutId: string) => {
      if (!layoutWindows.openLayout(layoutId)) return;
      const at = workspace.layouts.findIndex((l) => l.id === layoutId);
      if (at !== chosenIndex) return;
      const away = workspace.layouts.findIndex(
        (l) => l.id !== layoutId && !l.hidden && !layoutWindows.elsewhere.includes(l.id),
      );
      if (away >= 0) setLayoutIndex(away);
    },
    [layoutWindows, workspace.layouts, chosenIndex],
  );

  // In the desktop app, layout windows open at quit come back at launch — once
  // the workspace has answered, so their layouts are known.
  const restored = useRef(false);
  useEffect(() => {
    if (soleLayoutId || restored.current || status === "loading") return;
    restored.current = true;
    void desktopBridge()
      ?.restoredWindows?.()
      .then((names) => {
        for (const id of layoutsInWindows(
          names,
          workspace.layouts.map((l) => l.id),
        )) {
          layoutWindows.openLayout(id);
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  /**
   * Once the account is known: start usage tracking, and ask for the brand's
   * disclaimer if this person has not accepted it. Here, above the shell fork,
   * so both run on a phone as well — the tracker used to start inside the
   * desktop `Workspace` and never ran on the phone shell.
   */
  const [disclaimer, setDisclaimer] = useState<{ text: Disclaimer; actor: string } | null>(null);
  useEffect(() => {
    // The main window does both for the session; a layout window is not a
    // second visit and has already been asked.
    if (ROLE.kind === "layout") return;
    let alive = true;
    let stopActivity: (() => void) | null = null;
    void fetchMe().then(async (who) => {
      if (!alive || !who?.actorKey) return;
      // Usage tracking (docs/plans/usage-tracking.md): the first beat names the account.
      stopActivity = startActivity();
      // Site visits (docs/plans/site-visits.md): this browser's views are this account's.
      void claimVisits();
      const text = disclaimerFor(BRAND.key);
      // Asked again once EVERY_DAYS have passed since the latest yes.
      if (!text || stillAccepted(rememberedAccept(text.version, who.actorKey))) return;
      const last = await lastAccepted(text.version);
      if (last && stillAccepted(last)) {
        rememberAccept(text.version, who.actorKey, last);
        return;
      }
      if (alive) setDisclaimer({ text, actor: who.actorKey });
    });
    return () => {
      alive = false;
      stopActivity?.();
    };
  }, []);

  const acceptDisclaimer = useCallback(
    (method: AcceptMethod) => {
      if (!disclaimer) return;
      const { text, actor } = disclaimer;
      setDisclaimer(null);
      // Closed at once; remembered only once the server holds it, so a lost
      // write asks again next load rather than never.
      void recordAccept(text.version, text.brand, method).then((at) => {
        if (at) rememberAccept(text.version, actor, at);
      });
    },
    [disclaimer],
  );

  // Fin St's saved Years/Quarters, per chart — read by the data panel's table.
  const finStGrain = useMemo<FinStGrainStore>(
    () => ({
      get: (chartGroupId) => findChartGroup(workspace, chartGroupId)?.finStGrain ?? "years",
      set: (chartGroupId, grain) => dispatch({ type: "setFinStGrain", chartGroupId, grain }),
    }),
    [workspace, dispatch],
  );

  return (
    <FinStGrainContext.Provider value={finStGrain}>
    <ThemeProvider themeRef={workspace.themeRef} themes={themes}>
      {disclaimer && (
        <DisclaimerGate
          disclaimer={disclaimer.text}
          loading={status === "loading"}
          onAccept={acceptDisclaimer}
        />
      )}
      {/* The banner goes inside each branch rather than above both, because the
          mobile shell owns a fixed box that would paint over anything outside
          it. Above everything within its own shell either way: the point of the
          banner is that it cannot be scrolled past or covered by a chart. It
          renders nothing at all for anyone who is not an admin.

          The update bar sits above it for the same reasons, and takes a row
          rather than floating so it covers no control — see `UpdateBar`. */}
      {narrow ? (
        // The fixed box is here rather than inside `MobileApp`, so the banner
        // above keeps its row instead of being painted over — see SHELL there.
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            background: "var(--qf-background)",
          }}
        >
          <UpdateBar />
          <PauseBar />
          <ActingAsBar />
          <MobileApp
            workspace={workspace}
            dispatch={dispatch}
            themes={themes}
            onSaveTheme={saveTheme}
            layoutIndex={layoutIndex}
            setLayoutIndex={setLayoutIndex}
            // Holds the charting views back while the load is in flight, the
            // way `saveStatus` holds the desktop grid back below.
            saveStatus={status}
          />
        </div>
      ) : (
        <>
          <UpdateBar />
          <PauseBar />
          <ActingAsBar />
          <Workspace
            workspace={workspace}
            dispatch={dispatch}
            saveStatus={status}
            themes={themes}
            onSaveTheme={saveTheme}
            layoutIndex={layoutIndex}
            setLayoutIndex={setLayoutIndex}
            soleLayoutId={soleLayoutId}
            elsewhere={layoutWindows.elsewhere}
            onDetachLayout={ROLE.kind === "main" ? detachLayout : undefined}
            onFocusElsewhere={layoutWindows.focusLayout}
            remoteFocus={layoutWindows.remoteFocus}
          />
        </>
      )}
    </ThemeProvider>
    </FinStGrainContext.Provider>
  );
}

/**
 * The shell proper, inside the provider — so it can read the active theme from
 * context. Splitting it out is what lets `App` own the workspace state while
 * everything below it resolves colors from that same state's `themeRef`.
 */
function Workspace({
  workspace,
  dispatch,
  saveStatus,
  themes,
  onSaveTheme,
  layoutIndex,
  setLayoutIndex,
  soleLayoutId,
  elsewhere,
  onDetachLayout,
  onFocusElsewhere,
  remoteFocus,
}: {
  workspace: UserWorkspace;
  dispatch: Dispatch<WorkspaceAction>;
  saveStatus: SaveStatus;
  themes: Theme[];
  onSaveTheme: (theme: Theme) => void;
  /** Owned by `AuthenticatedApp`, shared with the phone shell. */
  layoutIndex: number;
  setLayoutIndex: Dispatch<SetStateAction<number>>;
  /** Layout windows (`lib/window-sync.ts`) — see `LayoutBar`'s props of the same names. */
  soleLayoutId: string | undefined;
  elsewhere: readonly string[];
  onDetachLayout: ((layoutId: string) => void) | undefined;
  onFocusElsewhere: (layoutId: string) => void;
  /** The layout window clicked last, which a detached data panel follows (rule 5). */
  remoteFocus: RemoteFocus | null;
}) {
  const resolveToken = useTokenResolver();

  /**
   * Whether the docked screener editor is open, and how wide.
   *
   * Deliberately component state rather than workspace data (D8): opening an
   * editor is not a layout change, and persisting it would reopen whatever panel
   * happened to be showing when a workspace was last saved.
   */
  const [editorOpen, setEditorOpen] = useState(false);
  /** Whether the saved-filter picker is showing. View state (D8). */
  /** Which builder tab to open on. Ephemeral (D8). */
  const [editorTab, setEditorTab] = useState<"columns" | "filters" | "format">("columns");
  const [editorSize, setEditorSize] = useState("1.4fr");

  /**
   * The right-click menu's anchor, and whether the theme editor is showing.
   *
   * Both are transient UI state (D8): a workspace records which theme is
   * selected, never that a menu happened to be open when it was saved.
   */
  /**
   * The DSL vocabulary, fetched once. Not workspace data and not per column —
   * it describes the language, so it is loaded here and passed down rather than
   * re-fetched by every editor that opens.
   */
  // Read once at mount: `localStorage` is synchronous, so the divider starts
  // where it was left rather than jumping there after a frame.
  const [columnsSplit, setColumnsSplit] = useState<number | undefined>(loadColumnsSplit);
  const [dslCatalog, setDslCatalog] = useState<DslCatalog | undefined>(undefined);
  useEffect(() => {
    void fetchDslCatalog().then(setDslCatalog);
  }, []);

  /**
   * Market closures and the day a date box opens on. Fetched once beside the
   * other catalogs; `emptyCalendar()` until it lands, so the toolbar renders
   * immediately rather than waiting on a round trip to show a date.
   */
  const [calendar, setCalendar] = useState<MarketCalendar>(emptyCalendar);
  // The screener's header marks read both, two components down
  // (lib/screener-marks.ts, docs/plans/screener-bands.md).
  useEffect(() => publishMarks({ holidays: calendar.holidays }), [calendar]);
  useEffect(() => publishMarks({ catalog: dslCatalog }), [dslCatalog]);

  /**
   * The symbol a plan is being written for, or `null`.
   *
   * Session state, not workspace state (D8): a half-written plan is not
   * something to restore on another device, and the plan itself is a journal
   * row rather than chart config.
   *
   * The account is fetched lazily, on first open, because a user who never
   * writes a plan should not cost a request — and it is the call that seeds the
   * paper account, so asking early would create one for someone who never wanted
   * it.
   */
  /**
   * The armed drawing tool, or `null`.
   *
   * Session state (D8), like the screener's shown/hidden: what you are *about*
   * to draw is not something to restore on another device, and the drawing it
   * produces is contract data in its own right.
   */
  const [armedTool, setArmedTool] = useState<AnnotationTool | null>(null);
  /** The armed financial-tools annotation. Its own slot — see `AnnotationTools`. */
  const [armedTrading, setArmedTrading] = useState<string | null>(null);
  /** Bumped by Clear — the half of it that only the chart can perform. */
  const [clearNonce, setClearNonce] = useState(0);

  /**
   * Whether a drawing has its first point down, waiting for a second click.
   *
   * Reported by whichever chart holds it. The only thing it changes is what
   * Escape means — see below.
   */
  const [placing, setPlacing] = useState(false);

  // Escape disarms. A modal pointer needs an exit that is not "find the lit
  // button again" — that button is 26px wide and on the far side of the chart
  // from wherever the pointer just was.
  //
  // **Except while a point is placed, when it takes the point back instead and
  // the tool stays armed.** Otherwise the one key that undoes a half-drawn line
  // also puts the tool away, and a user correcting a mis-click has to go and find
  // that 26px button again — which is the thing this listener exists to avoid.
  // The chart cancels the point itself; all this has to do is not disarm on the
  // same press. `placing` is the value from before the keystroke, which is
  // exactly the test wanted: something *was* placed, so this Escape belongs to it.
  useEffect(() => {
    if (armedTool === null && armedTrading === null) return;
    const cancel = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || placing) return;
      setArmedTool(null);
      setArmedTrading(null);
    };
    window.addEventListener("keydown", cancel);
    return () => window.removeEventListener("keydown", cancel);
  }, [armedTool, armedTrading, placing]);

  /**
   * The symbol search, if it is open — and **which chart the answer is for**.
   *
   * One dialog for the app, not one per chart toolbar. Nothing it renders
   * depends on which chart is selected; the only per-chart fact is where the
   * pick goes, so it is recorded here at the moment it is opened rather than
   * inferred later. That matters because the two ways in mean different
   * charts: clicking a box means *that* chart, and a letter typed at the page
   * means the selected one.
   *
   * Every toolbar used to render its own dialog, and app-wide typing needed a
   * module-scope subscriber set plus a prop to nominate which one answered —
   * which is how a keystroke ended up landing on an arbitrary instance and
   * being dropped. There is nothing left to nominate.
   */
  const [search, setSearch] = useState<{
    chartGroupId: string;
    query: string;
    select: boolean;
    /** The chart's symbol when the dialog opened: what Esc keeps. */
    keeps?: string;
  } | null>(null);

  /**
   * The chart whose symbol box is showing "DIA is locked · 🔓 Unlock" — the
   * answer to a symbol change the lock refused (`lib/symbol-lock.ts`). View
   * state, gone after a few seconds; `lockNoticeTimer` is what takes it away.
   */
  const [lockNotice, setLockNotice] = useState<string | null>(null);
  const lockNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * `chartSymbol`, for the action context built above its definition — Go to
   * routes through the very same `routePick` + `setSymbol` (or the toast).
   */
  const chartSymbolRef = useRef<((chartGroupId: string, symbol: string) => SymbolRoute) | null>(null);

  const [planningSymbol, setPlanningSymbol] = useState<string | null>(null);
  const [planAccountId, setPlanAccountId] = useState<string | null>(null);
  /** The plan being edited, or `null` for a new one. */
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [plansOpen, setPlansOpen] = useState(false);
  /** Bumped after a save so the panel and the chart's lines both re-read. */
  const [journalStamp, setJournalStamp] = useState(0);
  /**
   * The last close per symbol, accumulated as charts load.
   *
   * A map rather than one number because two things read it now: the plan form
   * seeds from the *active* chart, and the trades view needs a price for every
   * symbol it lists — several of which may not be charted at all, which is why
   * an absent entry has to be a case those callers handle rather than a bug.
   */
  const [prices, setPrices] = useState<ReadonlyMap<string, number>>(new Map());
  /**
   * The last bar's time per symbol, in unix seconds.
   *
   * Where a plan created from the *form* starts its levels. The draw tool takes
   * that from the gesture; the form has no gesture, and `createdAt` is a moment
   * during the session against bars stamped midnight — so without this a typed
   * plan starts past the end of the data and spans the chart instead of raying
   * from it.
   */
  const [barTimes, setBarTimes] = useState<ReadonlyMap<string, number>>(new Map());
  const rememberPrice = useCallback((symbol: string, price: number, at: number) => {
    const key = symbol.toUpperCase();
    setPrices((was) => {
      if (was.get(key) === price) return was;
      const next = new Map(was);
      next.set(key, price);
      return next;
    });
    setBarTimes((was) => {
      if (was.get(key) === at) return was;
      const next = new Map(was);
      next.set(key, at);
      return next;
    });
  }, []);

  /** The journal itself, kept whole — the chart's lines and the trades view are
   *  two readings of it, and fetching it twice would be the same request. */
  const [journal, setJournal] = useState<{
    plans: Plan[];
    trades: Trade[];
    executions: Execution[];
  }>({ plans: [], trades: [], executions: [] });
  const [showTrades, setShowTrades] = useState(false);
  const [tradeGroup, setTradeGroup] = useState<TradeGroup>("plan");


  useEffect(() => {
    // **On mount, not when the form opens.** Three things need the account
    // now — the form, the trades view, and the chart's own draw-a-plan tool —
    // and the last of those has no dialog to hang a fetch on. Gated on the
    // form, a plan drawn before the form had ever been opened had no account
    // to belong to and was silently dropped.
    if (planAccountId !== null || !journalEnabled()) return;
    let cancelled = false;
    fetchAccount()
      .then((account) => {
        if (!cancelled) setPlanAccountId(account.id);
      })
      .catch(() => {
        // Left null, which keeps the form closed and the draw tool inert. A
        // plan with no account to size against is a save the server refuses.
        if (!cancelled) setPlanningSymbol(null);
      });
    return () => {
      cancelled = true;
    };
  }, [planAccountId]);
  useEffect(() => {
    void fetchMarketCalendar().then(setCalendar);
  }, []);

  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  /**
   * Which chart a template dialog is about, and which dialog.
   *
   * The **id**, not the group: the menu closes before the dialog opens and the
   * workspace can change underneath it, so holding the object would apply a
   * template onto a chart as it was when the menu was right-clicked. Resolved
   * at render, and the dialog closes itself if the chart is gone.
   */
  const [templateFor, setTemplateFor] = useState<
    { mode: "save" | "apply"; groupId: string } | null
  >(null);
  /**
   * The plan level the menu was opened on, when it was opened on one.
   *
   * Kept beside `menuAt` rather than inside it because the ordinary chart menu
   * has no level and the two open the same component — a menu that had to carry
   * a nullable level in every case would put the check at every item instead of
   * once where the items are built.
   */
  const [menuLevel, setMenuLevel] = useState<ReturnType<typeof parseLevelId>>(null);
  const [themeEditorOpen, setThemeEditorOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [dataHealthOpen, setDataHealthOpen] = useState(false);
  // **Stripe sends the browser back to `/?billing=…`** after a checkout or the
  // portal. Opening the plan screen on arrival is what says "it worked" (or
  // that it was cancelled); the parameter is stripped at once so a refresh does
  // not replay it.
  const [billingReturn] = useState(() => {
    const returned = readBillingReturn(window.location.search);
    if (returned !== null) {
      window.history.replaceState({}, "", withoutBillingReturn(window.location.href));
    }
    return returned;
  });
  // **A plan chosen on the landing page** opens Your plan on that card; a
  // checkout's return outranks it.
  const [planIntent] = useState(() => (billingReturn === null ? takePlanIntent() : null));
  const [planOpen, setPlanOpen] = useState(billingReturn !== null || planIntent !== null);
  const [groupRequestOpen, setGroupRequestOpen] = useState(false);
  const [booksOpen, setBooksOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  /**
   * The screener presets this account may apply, and where a published one goes.
   *
   * Loaded once beside the other catalogs. `publishTarget` is the group a
   * publish would belong to, and the server names it: `publishTo` is set only
   * while acting as a group's own account. It was `orgs[0]` for any admin, and
   * `orgs` is sorted by key — so an account in `qf-in` and `grp-jp` published
   * JP's layouts to every India member (`docs/plans/group-accounts.md` §1).
   */
  /** The tab strip's catalog — the same rows, read through the store. */
  const { screens } = useScreens();
  const [publishTarget, setPublishTarget] = useState<string | null>(null);
  /** The publish target as the author reads it — `JP`, not `grp-jp`. */
  const [publishTargetName, setPublishTargetName] = useState<string | null>(null);
  /** The groups this account belongs to — which group layouts it receives. */
  const [memberOf, setMemberOf] = useState<readonly string[] | null>(null);
  /** The group accounts this account may author — where Promote & Publish can go. */
  const [groupAccounts, setGroupAccounts] = useState<readonly GroupAccount[]>([]);
  /** Each virtual group this account is in, with what it includes (`group-sharing.md`). */
  const [virtualGroups, setVirtualGroups] = useState<Readonly<Record<string, readonly string[]>>>({});

  useEffect(() => {
    let alive = true;
    void fetchMe().then((who) => {
      if (!alive) return;
      setPublishTarget(who?.publishTo ?? null);
      setPublishTargetName(
        who?.groupAccounts.find((g) => g.group === who.publishTo)?.name ?? who?.publishTo ?? null,
      );
      // `memberOf`, not `orgs`: a virtual group's member receives the layouts
      // of every group it includes, and only `memberOf` names those.
      setMemberOf(who?.memberOf ?? []);
      setGroupAccounts(who?.groupAccounts ?? []);
      setVirtualGroups(who?.virtualGroups ?? {});
      // JAS members draw hollow candles unless a chart says otherwise.
      setCandleDefaultFromGroups(who?.orgs ?? []);
      // The Sim button exists only for an admin — see `ScreenerBuilderProps`.
      setIsAdmin(Boolean(who?.isAdmin));
    });
    return () => {
      alive = false;
    };
  }, []);

  /**
   * The screen on the panel, for `publishToClub` — declared above the section
   * lookup it reads, so it is a ref written beside that lookup each render.
   */
  const screenerRef = useRef<string | undefined>(undefined);

  const publishToClub = useCallback(
    async (input: {
      name: string;
      description: string;
      columns: ScreenerConfig["columns"];
      filter: ScreenerConfig["filter"];
      sort: ScreenerConfig["sort"];
      recs?: ScreenerConfig["recs"];
      topDown?: ScreenerConfig["topDown"];
      seasonality?: ScreenerConfig["seasonality"];
    }) => {
      if (!publishTarget) return;
      // **A screen that exists is republished under its own key**, through its
      // draft when it is the one on the panel; only a new name is slugged.
      // `publishPreset` alone wrote *Persons* to `grp-jp-persons`, beside
      // `jp-persons` (`docs/plans/author-screen-drafts.md`, step 5).
      await publishFromEditor({
        ...input,
        publishTo: publishTarget,
        screens: screensSnapshot(),
        showing: screenerRef.current,
      });
      // **Re-read the tab catalog, which is where a published screen now
      // appears.** This used to refresh the Presets tab's list; that list has
      // gone to the tab strip, so publishing puts the club's screen in front of
      // its members — and the author — in the same place they select every
      // other screen. Re-read rather than pushed in locally: the server derives
      // the key and may have replaced a preset of the same name, so what it
      // returns is the only accurate answer.
      await loadScreens();
    },
    [publishTarget],
  );
  // The Seasonality screen's own Publish — it has no builder, so no Publish tab.
  const [seasPublish, setSeasPublish] = useState<"idle" | "busy" | "failed">("idle");

  /**
   * Which layout is on screen.
   *
   * View state, not workspace data: which tab you last had open is a property
   * of this browser session, not of the workspace, and writing it would make
   * every tab click a save. Clamped on read so a workspace that shrinks — a
   * layout deleted elsewhere — falls back to the first rather than rendering
   * nothing.
   *
   * Declared here, above the group-layout sync, which needs to know the layout
   * on screen: an in-session replace waits until the member leaves it.
   */
  // `layoutIndex` is a prop: see `AuthenticatedApp`.
  // Clamped, then moved off a hidden layout. Both corrections are for state
  // that arrives from outside this component: a workspace that shrank, and one
  // whose first layout was hidden on another machine — neither of which the tab
  // strip has a tab for, so without this the shell renders a layout the user
  // cannot see they are in. `nearestShown` falls back rather than failing, so a
  // workspace with everything hidden still renders something.
  const index = nearestShown(
    workspace.layouts,
    Math.min(layoutIndex, workspace.layouts.length - 1),
  );
  const layout = workspace.layouts[index];

  /**
   * What the builder's **Copy from screen…** lists and loads while authoring:
   * every screen in the catalog, named by owner as the picker groups them.
   */
  const copySources = useMemo<CopyFromScreen>(
    () => ({
      screens: screens.map((s) => ({
        key: s.key,
        name: s.name,
        ownerName:
          s.ownerKind === "org" && s.ownerKey
            ? groupDisplayName(s.ownerKey, s.ownerName)
            : s.ownerKind === "system"
              ? "Built-in"
              : "This account",
      })),
      load: screenLoader(publishTarget),
    }),
    [screens, publishTarget],
  );

  /** Group display names the screen catalog already knows — `JAS` for `grp-jas`. */
  const groupNames = useMemo(
    () =>
      new Map(
        screens.flatMap((s) => (s.ownerKey && s.ownerName ? [[s.ownerKey, s.ownerName] as const] : [])),
      ),
    [screens],
  );

  /**
   * The layout presets this account may apply, and a member's group layouts
   * kept in step with them — adopted, replaced, linked when applied by hand,
   * and the one-time question about an unlinked copy
   * (`api/group-layout-sync.ts`, `docs/plans/group-layout-sync.md`).
   *
   * Gated by the same `publishTarget` as the screener's: one answer to "who
   * may publish", not two. Publishing re-reads the listing rather than pushing
   * the new one in locally, because the server may have replaced an
   * arrangement of the same name.
   */
  const {
    layoutPresets,
    refresh: refreshLayoutPresets,
    applyPreset: applyLayoutPreset,
    publish: publishToClubLayout,
    prompt: legacyLayoutPrompt,
    answer: answerLegacyLayout,
    update: sharedUpdate,
    answerUpdate: answerSharedUpdate,
    promote: promoteLayout,
  } = useGroupLayoutSync({
    workspace,
    dispatch,
    // **`saveStatus`, the prop.** This read a bare `status` from 4b52cea7 on —
    // and `Workspace` has no `status`, so it resolved to the DOM's
    // `window.status` (always `""`), and adoption and replace returned early on
    // every render. Typing the hook's input is what surfaced it.
    status: saveStatus,
    memberOf,
    virtualGroups,
    publishTarget,
    activeIndex: index,
    select: setLayoutIndex,
    groupNames,
  });

  /** Shared with me's ● new: each group layout's state for this member. */
  const sharedStates = useMemo(() => {
    const out = new Map<string, SharedState>();
    for (const preset of layoutPresets) {
      out.set(preset.key, sharedState(preset, workspace, memberOf ?? [], publishTarget, virtualGroups));
    }
    return out;
  }, [layoutPresets, workspace, memberOf, publishTarget, virtualGroups]);

  /**
   * Authoring a virtual group (`docs/plans/group-sharing.md`, mock 2): the
   * layouts it inherits, read-only, each with its screener override. Absent for
   * every other account — an ordinary group has nothing to inherit.
   */
  const includedGroups = publishTarget ? virtualGroups[publishTarget] : undefined;
  const inheritedLayouts = includedGroups ? (
    <InheritedLayouts
      includes={includedGroups.map((key) => ({ key, name: groupDisplayName(key, null, groupNames) }))}
      layoutPresets={layoutPresets}
      ownScreens={screens
        .filter((s) => s.ownerKind === "org" && s.ownerKey === publishTarget)
        .map((s) => ({ key: s.key, name: s.name }))}
      screenName={(key) => screens.find((s) => s.key === key)?.name ?? key}
      loadLayout={fetchLayout}
      loadOverrides={fetchLayoutOverrides}
      saveOverride={saveLayoutOverride}
    />
  ) : undefined;

  /**
   * Keep the arrangement on screen as one of the caller's own templates.
   *
   * Re-reads the catalog rather than pushing the new row in by hand: the server
   * mints the key and normalises the name, so the row it returns is the truth
   * and inventing a local one would be a second answer that drifts the first
   * time either changes. The same call `publishToClubLayout` makes below, for
   * the same reason.
   */
  const saveOwnTemplate = useCallback(
    async (input: { name: string; layout: Layout }) => {
      await saveLayoutTemplate(input);
      await refreshLayoutPresets();
    },
    [refreshLayoutPresets],
  );

  const removeOwnTemplate = useCallback(
    async (key: string) => {
      await deleteLayoutTemplate(key);
      await refreshLayoutPresets();
    },
    [refreshLayoutPresets],
  );

  /**
   * Withdraw a layout this club holds.
   *
   * **Parked rather than deleted**, which is the reversible half of the pair
   * and the one a mis-click should reach: the key survives, so re-publishing
   * puts it back and the grants naming it were never re-issued. Deleting stays
   * in the admin panel, where it is next to the warning it deserves.
   */
  const unpublishLayout = useCallback(
    async (key: string) => {
      // `/author`, not `/admin`: a Group Admin withdraws too, and only their own.
      await withdrawPublished("layouts", key);
      await refreshLayoutPresets();
    },
    [refreshLayoutPresets],
  );

  // Applying a preset is `applyLayoutPreset`, from the hook above. **Added,
  // never replacing**: an arrangement re-shapes the layout you are in because
  // choosing a shape is what that means; a published layout is a different
  // desk, and dropping it over the one on screen would destroy charts somebody
  // configured. A group's is added *linked* (step 3), so its republish reaches
  // the copy.

  /**
   * A date typed into a chart's toolbar, on its way down to that chart.
   *
   * View state, not workspace data (D8): a workspace records how many bars a
   * chart shows, never that somebody once went to look at March. The nonce is
   * what makes asking twice mean twice — the second ask usually follows a pan
   * away from the first, and a chart that ignored it would look broken in
   * exactly the case the box is for.
   */
  const [goTo, setGoTo] = useState<{
    chartGroupId: string;
    date: string;
    nonce: number;
  } | null>(null);

  // Published by ScreenerView as snapshots arrive; the docked editor is a
  // sibling in the layout, not a child, so the schema has to come up and back.
  const [screenerSchema, setScreenerSchema] = useState<ScreenerSchema>({
    fields: [],
    meta: [],
  });

  // Admin-only controls. False until `/auth/me` answers, deliberately the
  // opposite way round from `lib/entitlements.ts`'s permissive default: a
  // capability appearing late reads as the app taking it away, but an *admin*
  // control appearing late is simply correct, and showing one to a
  // non-admin — even briefly — is the failure worth avoiding.
  const [isAdmin, setIsAdmin] = useState(false);
  // Seasonality Basic or Pro: without either, its screens are hidden, not locked.
  const { can: holds } = useEntitlements();
  const seasonalityTier = tierOf(holds);
  const [simulating, setSimulating] = useState(false);
  // QueVee Voice's Train (docs/plans/quevee-voice.md §6): the voices this
  // account may train — asked only of an admin or a Group Admin, and offered
  // only when one comes back — and the chart a Train dialog is open on.
  const [voices, setVoices] = useState<Voice[]>([]);
  const [training, setTraining] = useState<{ chartGroupId: string; symbol: string; frequency: Frequency } | null>(null);
  const mayTrain = isAdmin || groupAccounts.length > 0;
  useEffect(() => {
    if (!mayTrain) {
      setVoices([]);
      return;
    }
    let live = true;
    void fetchVoices().then((found) => live && setVoices(found));
    return () => {
      live = false;
    };
  }, [mayTrain]);

  /**
   * The chart section the toolbar drives, and the one a screener row charts to.
   *
   * Session state, like `layoutIndex`: which pane you last clicked is not a
   * property of the workspace, and persisting it would make every click a save.
   *
   * Held as an id rather than an index because sections are reordered and
   * removed — an index would silently retarget a different chart when a layout
   * changes shape, which is exactly the bug that is hardest to see.
   */
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // The fallback rule lives in `lib/active-section` with its edge cases —
  // a stale id, a screener, a section from another layout — because
  // `SectionGrid` reaches SciChart and cannot be rendered in a test.
  const chartSection = activeChartSection(layout?.sections, activeSectionId);

  /**
   * Every chart on this layout, in cell order — what the PIP's two rules are
   * decided from.
   *
   * `chartSections` is the same helper the active-section fallback uses, so the
   * two cannot disagree about what counts as a chart.
   */
  const chartIds = useMemo(
    () =>
      // Chart **group** ids, not section ids: the two differ, and every PIP
      // field lives on the group. `chartSections` is the same helper the
      // active-section fallback uses, so the two cannot disagree about what
      // counts as a chart.
      chartSections(layout?.sections)
        .map((s) => chartGroupConfig(s)?.id)
        .filter((id): id is string => Boolean(id)),
    [layout?.sections],
  );

  /**
   * One PIP patch, turned into the dispatches it means.
   *
   * The patch arrives from the panel's own bar with exactly one key set, which
   * is why it is a patch and not four callbacks threaded through the grid.
   *
   * **`swap` is the one that is two mutations.** The chart takes the panel's
   * interval and the panel takes the rung above that — a straight exchange would
   * put the chart's *finer* interval in the panel, which is the one state the
   * whole ladder exists to prevent. `swapWithChart` decides both values and
   * refuses the pair it cannot promote, so this either makes both changes or
   * makes none: half a swap is a chart and a panel showing the same bars.
   */
  const applyPip = useCallback(
    (chartGroupId: string, patch: PipPatch) => {
      if (patch.swap) {
        const target = chartSections(layout?.sections)
          .map((sec) => chartGroupConfig(sec))
          .find((g) => g?.id === chartGroupId);
        if (!target) return;
        const shown = pipFrequencyFor(target.frequency, target.pipFrequency);
        const swapped = shown ? swapWithChart(target.frequency, shown) : null;
        if (!swapped) return;
        dispatch({ type: "setFrequency", chartGroupId, frequency: swapped.chart });
        dispatch({ type: "setPipFrequency", chartGroupId, frequency: swapped.pip });
        return;
      }
      // `!== undefined` rather than truthiness: `null` is a real value on two of
      // these — it closes the panel, and it unpins the interval so it follows
      // the chart again — and a truthy check would silently drop both.
      if (patch.view !== undefined) {
        dispatch({ type: "setPipView", chartGroupId, view: patch.view });
      }
      if (patch.frequency !== undefined) {
        dispatch({ type: "setPipFrequency", chartGroupId, frequency: patch.frequency });
      }
      if (patch.corner !== undefined) {
        dispatch({ type: "setPipCorner", chartGroupId, corner: patch.corner });
      }
      if (patch.size !== undefined) {
        dispatch({ type: "setPipSize", chartGroupId, size: patch.size });
      }
    },
    [dispatch, layout?.sections],
  );

  /**
   * Which chart the data panel is pointed at, or `null` when it is closed.
   *
   * **One panel for the layout, re-pointed rather than duplicated.** Pressing
   * the other chart's key moves this one; a second panel would mean two things
   * claiming to be *the* data panel and the user deciding which they are looking
   * at. Pressing the key of the chart it already shows closes it, which is what
   * a toggle on a lit key means.
   *
   * View state (D8) like `plansOpen`: nothing here reaches the workspace, so
   * opening a panel is not a save.
   */
  const [dataPanelId, setDataPanelIdRaw] = useState<string | null>(null);

  /**
   * The dock — the screener's cell, holding one or two panels
   * (`docs/plans/split-view.md`). **Workspace data, per layout**: which panels
   * show and their share are saved, so they come back on reload and on another
   * machine. `layoutHasDock` is the saved layout's answer — a hidden dock is
   * still a dock, it just shows nothing.
   */
  const dock = dockOf(layout);
  const layoutHasDock = layout?.sections.some((s) => s.kind === "screener") ?? false;
  const setDock = useCallback(
    (next: Dock) => {
      if (layout) dispatch({ type: "setDock", layoutId: layout.id, dock: next });
    },
    [layout, dispatch],
  );
  const dataDocked = layoutHasDock && isShowing(dock, "data");

  /**
   * Community (docs/plans/community.md): a panel in the dock, like the data
   * panel. A layout with no dock has nowhere to put it, so there it floats as a
   * dialog — the data panel's fallback, for its reason.
   */
  const communityOn = available("community") && communityEnabled();
  const communityDocked = layoutHasDock && isShowing(dock, "community");
  const [communityFloating, setCommunityFloating] = useState(false);
  /** Bumped by "Share to group": the panel opens its composer with the chart on it. */
  const [shareNonce, setShareNonce] = useState(0);

  /**
   * The QueVee chat (docs/features/chat.md): a dock panel like Community, with
   * Community's floating fallback on a layout with no dock. Both gates — the
   * brand's `agent` feature and the plan's `feat:agent` — and a quevee to talk
   * to. `aiAsk` is Ask AI's text, handed to the panel to send once it is up.
   */
  const chat = chatGate("desktop", chatConfigured());
  const chatOn = chat.panel;
  const aiDocked = layoutHasDock && isShowing(dock, "ai");
  const [aiFloating, setAiFloating] = useState(false);
  const [aiAsk, setAiAsk] = useState<{ text: string; nonce: number } | undefined>(undefined);

  /**
   * Whether the panel is in its own window.
   *
   * A boolean, and the window itself belongs to `DetachedWindow`: open and close
   * have to be the same effect's setup and cleanup or StrictMode's
   * mount → cleanup → mount closes the window a moment after it appears — which
   * is exactly what it did when this held the `Window`.
   */
  const [detached, setDetached] = useState(false);

  /**
   * Views of the panel out in windows of their own — Fin St on one monitor,
   * News on another (owner, 2026-10-28). View state like `detached`; each is
   * its own window, named per view (`viewWindowName`), and follows the same
   * chart the panel does.
   */
  const [detachedViews, setDetachedViews] = useState<DataTab[]>([]);

  /**
   * The chart the panel describes: the one it was pointed at while that is on
   * screen, else — docked or detached — the active one. `lib/data-panel-target.ts`
   * has the rule and why a detached panel follows a layout switch.
   */
  const activeChartId = chartSection ? chartGroupConfig(chartSection)?.id : undefined;
  const panelWindowed = detached || detachedViews.length > 0;
  /**
   * **Rule 5: a detached panel follows the window clicked last** (owner,
   * 2026-10-28). With Layout 2 open in a window of its own and clicked there,
   * the panel in its window describes Layout 2's chart, though it lives in this
   * window. A docked panel is part of this window's layout and never follows.
   */
  const remoteLayout =
    panelWindowed && remoteFocus ? workspace.layouts.find((l) => l.id === remoteFocus.layoutId) : undefined;
  const layoutCharts = chartSections((remoteLayout ?? layout)?.sections)
    .map((sec) => chartGroupConfig(sec))
    .filter((g): g is NonNullable<typeof g> => !!g);
  const dataTargetId = dataPanelTarget({
    pointed: dataPanelId,
    chartIds: layoutCharts.map((g) => g.id),
    activeChartId: remoteLayout ? (remoteFocus?.chartId ?? layoutCharts[0]?.id) : activeChartId,
    docked: dataDocked && !remoteLayout,
    detached: panelWindowed,
  });
  const setDataPanelId = setDataPanelIdRaw;

  // **Every window with a chart on screen says when it is clicked**, with its
  // active chart: the main window's detached data panel follows a layout window
  // (rule 5 above), and a data layout's cells follow whichever chart was clicked
  // last, in any window (`useLastFocus`). A layout with no chart says nothing —
  // clicking a data cell must not make the cells follow nothing.
  const layoutId = layout?.id;
  const layoutHasChart = chartSections(layout?.sections).length > 0;
  const activeChartRef = useRef(activeChartId);
  activeChartRef.current = activeChartId;
  useEffect(() => {
    if (!layoutId || !layoutHasChart) return;
    const tell = () => announceFocus(layoutId, activeChartRef.current ?? null);
    window.addEventListener("pointerdown", tell, true);
    window.addEventListener("focus", tell);
    return () => {
      window.removeEventListener("pointerdown", tell, true);
      window.removeEventListener("focus", tell);
    };
  }, [layoutId, layoutHasChart]);
  // A different chart made active, or another layout shown, is a new target too.
  useEffect(() => {
    if (layoutId && layoutHasChart && document.hasFocus()) announceFocus(layoutId, activeChartId ?? null);
  }, [layoutId, layoutHasChart, activeChartId]);

  /**
   * The chart a data cell follows (docs/features/pip-and-data-panel.md, *Data
   * layouts*): in a layout with charts, its active one; in a data-only layout,
   * the chart clicked last in any window — or, before any click, the first
   * chart of the workspace, so a fresh launch onto a data layout shows something.
   */
  const lastFocus = useLastFocus();
  const focusByLayout = useFocusByLayout();
  const followId = isDataOnly(layout) ? followOf(layout) : undefined;
  const tiedTo = followId
    ? workspace.layouts.find((l) => l.id === followId && chartSections(l.sections).length > 0)
    : undefined;
  const followedChart = useMemo(() => {
    // Its own layout's charts — not `layoutCharts`, which rule 5 may point at
    // another window's layout for the detached panel.
    if (layoutHasChart) {
      const own = chartSections(layout?.sections).map((sec) => chartGroupConfig(sec));
      return own.find((g) => g?.id === activeChartId) ?? own[0];
    }
    // **Following one layout** (▾ › Follow): that layout's chart clicked last,
    // else its first — clicks anywhere else move nothing. A layout since
    // deleted, or left with no chart, falls through to the chart clicked last.
    if (tiedTo) {
      const own = chartSections(tiedTo.sections).map((sec) => chartGroupConfig(sec));
      return own.find((g) => g?.id === focusByLayout[tiedTo.id]) ?? own[0];
    }
    const all = workspace.layouts.flatMap((l) =>
      chartSections(l.sections).map((sec) => ({ layoutId: l.id, group: chartGroupConfig(sec) })),
    );
    const heard = lastFocus
      ? (all.find((c) => c.layoutId === lastFocus.layoutId && c.group?.id === lastFocus.chartId) ??
        all.find((c) => c.layoutId === lastFocus.layoutId))
      : undefined;
    return (heard ?? all[0])?.group;
  }, [layoutHasChart, activeChartId, layout, workspace.layouts, lastFocus, tiedTo, focusByLayout]);

  /** What the tab says after the name: `NVDA`, or `NVDA → Two Charts` when tied (owner). */
  const dataNote =
    isDataOnly(layout) && followedChart?.symbol
      ? `${followedChart.symbol}${tiedTo ? ` → ${tiedTo.name}` : ""}`
      : undefined;
  const followOptions = useMemo(
    () => workspace.layouts.filter((l) => chartSections(l.sections).length > 0).map((l) => ({ id: l.id, name: l.name })),
    [workspace.layouts],
  );

  // A layout window is named for its layout in the taskbar and Alt+Tab — and a
  // data layout for the symbol it follows too, as its tab is.
  const windowTitle = soleLayoutId && layout ? `${layout.name}${dataNote ? ` · ${dataNote}` : ""}` : undefined;
  useEffect(() => {
    if (windowTitle) document.title = `${windowTitle} — ${BRAND.identity.name}`;
  }, [windowTitle]);
  const dataPanelSymbol = dataTargetId ? layoutCharts.find((g) => g.id === dataTargetId)?.symbol : undefined;

  /**
   * Whether this browser has refused to open a window.
   *
   * Not a preference and not persisted: it is a fact about the browser, learned
   * the only way it can be. `canDetach` cannot see it — a probe before a click
   * has no user activation and answers "refused" everywhere — so the first
   * detach is the test, and DuckDuckGo is the browser that fails it, declining
   * even a bare `open("", name)` with no features asked for.
   */
  const [detachRefused, setDetachRefused] = useState(false);

  /**
   * Where the key that opened the panel is, so the hover form can sit beside it.
   *
   * Recorded per gesture rather than tracked: a rect held across a resize or a
   * layout change anchors the panel to where the key used to be.
   */
  const [dataAnchor, setDataAnchor] = useState<DOMRect | null>(null);

  /**
   * The strip's data key, measured at the moment of the gesture.
   *
   * On the wrapper rather than the button because the pointer handlers are
   * there: `GridToolbarButton` is a plain key with no pointer surface of its
   * own, and the two neighbours that open popovers wrap it the same way.
   */
  const dataKeyRef = useRef<HTMLDivElement | null>(null);

  /**
   * Which tab the data panel shows.
   *
   * Held here rather than inside the panel because the action registry needs to
   * set it: "open the fundamentals" is one call that opens the panel *and* names
   * a tab. It also keeps the behaviour that made the tab worth having — one
   * value across a re-point, so comparing two symbols stays one click.
   */
  const [dataTab, setDataTab] = useState<DataTab>("profile");

  /**
   * Hover to peek, click to keep.
   *
   * The timing lives in the hook because the interesting moment is the gap
   * between the key and the panel: leaving only *schedules* a close, and
   * entering the panel cancels it. Closing on leave outright makes the panel
   * vanish on the way to it, every time.
   */
  /**
   * The two toolbar keys that reveal a list rather than act on one thing.
   *
   * Hover shows, click pins — `dataHover`'s pairing below, and the strip is
   * better for having one rule across it than for each key inventing its own.
   * The screener key keeps its click for the editor, because opening the editor
   * is what that key has always meant; the conditions key had only a click, and
   * a list you have to click to read is one gesture more than looking at it.
   */
  const screensHover = useHoverIntent();
  const conditionsHover = useHoverIntent();

  const dataHover = useHoverIntent({
    onClose: () => {
      // A hover that ends puts everything back, including a window: the panel is
      // no longer on screen to bring one home.
      setDataPanelId(null);
      setDetached(false);
    },
  });
  // `Workspace` is the desktop shell, so `narrow` is false here by construction
  // — asked anyway rather than assumed, because the rule that decides this is
  // `canDetach` and half of it applied in the caller is not that rule.
  const narrow = useIsNarrow();
  const detachable = useMemo(() => canDetach(narrow, isStandalone()), [narrow]);

  /** What the panel's tab strip needs to detach a view (`components/view-detach.ts`). */
  const viewDetach = useMemo<ViewDetach | null>(
    () =>
      detachable
        ? {
            detached: detachedViews,
            detach: (view) => setDetachedViews((was) => (was.includes(view) ? was : [...was, view])),
            // Opening a name that is already open returns that window and
            // raises it; it never makes a second.
            focus: (view) => void openDataWindow(undefined, viewWindowName(view)),
          }
        : null,
    [detachable, detachedViews],
  );
  const dataPanelName = useSymbolName(dataPanelSymbol ?? "");

  /** Close the panel wherever it is — docked, or in its own window. */
  const closeDataPanel = useCallback(() => {
    dataHover.dismiss();
    setDataPanelId(null);
    setDetached(false);
    if (dataDocked) setDock(closePanel(dock, "data"));
  }, [dataHover, setDataPanelId, dataDocked, dock, setDock]);

  /**
   * Into its own window. A docked panel **leaves the dock** — the window is view
   * state, and a half kept empty for it would be a dock showing nothing — and is
   * pinned, because the window is rendered from the pinned state.
   */
  const detachDataPanel = useCallback(() => {
    // Pointed first: a docked panel nobody aimed follows the active chart only
    // *because* it is docked, so once it leaves the dock it would describe
    // nothing — and the window, which renders only with a symbol, opened on the
    // next hover of the data key instead of on this click.
    if (dataTargetId) setDataPanelId(dataTargetId);
    if (dataDocked) setDock(closePanel(dock, "data"));
    if (!dataHover.pinned) dataHover.togglePin();
    setDetached(true);
  }, [dataTargetId, setDataPanelId, dataDocked, dock, setDock, dataHover]);

  /**
   * In the desktop app, the pop-out comes back at launch if it was out when the
   * app quit (owner, 2026-10-28: every window reopens where it was). The shell
   * keeps that list (`lib/desktop.ts` says why the page cannot), and puts the
   * window where it was. A browser has no shell and asks nothing — and would
   * block an open that no click asked for anyway.
   *
   * Once, at mount: `live` drops StrictMode's first run, because `togglePin` is
   * a toggle and two answers would leave the panel unpinned and the window shut.
   */
  useEffect(() => {
    let live = true;
    void desktopBridge()
      ?.restoredWindows?.()
      .then((names) => {
        if (!live) return;
        const views = viewsInWindows(
          names,
          SYMBOL_VIEWS.map((v) => v.id),
        );
        if (views.length > 0) setDetachedViews(views);
        if (!names.includes(DATA_WINDOW_NAME)) return;
        dataHover.togglePin();
        setDetached(true);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Home from the window. With a dock it comes back **beside** what is there,
   * never over it: it left to be read elsewhere, and returning should not take
   * the screener's place.
   */
  const dockDataPanel = useCallback(() => {
    setDetached(false);
    if (layoutHasDock) {
      if (dataHover.pinned) dataHover.togglePin();
      setDock(addPanel(dock, "data"));
    }
  }, [layoutHasDock, dataHover, dock, setDock]);

  /**
   * The app, as the action registry sees it.
   *
   * Every `run` in `lib/actions.ts` is a dispatch made somewhere in this file
   * already; this is the seam that lets a second and third caller — QueVee, and
   * a command search — make the same ones. The registry stays pure and testable
   * because everything it touches arrives through here.
   */
  const screenerLinksRef = useRef<ReturnType<NonNullable<ActionContext["screenerLinks"]>>>(undefined);
  const actionContext = useMemo<ActionContext>(
    () => ({
      activeChartGroupId: chartSection ? chartGroupConfig(chartSection)?.id : undefined,
      chartIds,
      chartGroup: (id) =>
        chartSections(layout?.sections)
          .map((sec) => chartGroupConfig(sec))
          .find((g) => g?.id === id),
      dispatch,
      mintId: mintUlid,
      indicators: cachedCatalog,
      dataPanel: {
        openOn: (chartGroupId, tab) => {
          setDataPanelId(chartGroupId);
          if (tab) setDataTab(tab);
          // With a dock, beside what is there — a request for the fundamentals
          // should not take the screener away.
          if (layoutHasDock) {
            setDock(addPanel(dock, "data"));
            return;
          }
          // Opened by a call rather than by a pointer, so it is pinned: there is
          // no hover holding it, and a panel that closed on the next mouse move
          // would be a request that appeared to do nothing.
          if (!dataHover.pinned) dataHover.togglePin();
        },
        setTab: setDataTab,
        close: closeDataPanel,
        dock: dockDataPanel,
        detach: detachDataPanel,
      },
      // The command search's additions. Each reads its answer when called, not
      // when this is built: a chart's table, the lists and the plan all move
      // without this context being rebuilt.
      chartTable,
      download: (text, fileName) => {
        const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      },
      copyText: (text) => {
        // Called inside the click, which is what the browser asks of a write.
        // A refusal is rare and has nowhere better to go than the console: the
        // palette has already closed on the row that asked for it.
        void navigator.clipboard?.writeText(text).catch((cause: unknown) => {
          // eslint-disable-next-line no-console
          console.warn("[copy symbol] the browser refused the clipboard", cause);
        });
      },
      screenerCommands,
      trackLists: () => (trackListsEnabled() && available("tracklists") ? trackListsSnapshot() : []),
      // The star column's two answers — the same gate the screener's ⋯ menu
      // puts on Import and Paste.
      trackListsOffered: () => trackListsEnabled() && available("tracklists"),
      describer: () => (describeEnabled() ? describeExpr : undefined),
      // The **saved** screener, as `savedScreenerSection` reads it below: a
      // hidden panel still has a library a described condition can join.
      screener: () => {
        const section = layout?.sections.find((s) => s.kind === "screener");
        const config = section && screenerConfig(section);
        return section && config ? { sectionId: section.id, config } : undefined;
      },
      // Through a ref: the links resolve further down, after this context.
      screenerLinks: () => screenerLinksRef.current,
      compileExpr,
      openScreenerEditor: (tab) => {
        setEditorTab(tab);
        setEditorOpen(true);
      },
      // Go to: the symbol search's "chart this" path, through a ref because
      // `chartSymbol` is defined below and reads the workspace as it stands.
      chartSymbol: (chartGroupId, symbol) =>
        chartSymbolRef.current?.(chartGroupId, symbol) ?? { kind: "locked", chartGroupId, symbol },
      // Absent with no symbol service, which charts what is typed — the dialog's rule.
      ...(symbolsConfigured ? { symbols: symbolIndex } : {}),
      // Ask AI: a chat surface must have published its opener, and the brand
      // and the plan must both offer QueVee (`available("agent")`).
      askAi: () => (available("agent") ? chatOpener() : undefined),
      // The shipped guide, searched by the palette's Guide rows and the agent's
      // findInGuide — same origin, part of this build (`api/guide-index.ts`).
      guide: guideIndex,
      loadGuide: loadGuideIndex,
      openGuideAt,
    }),
    [
      chartSection,
      chartIds,
      layout?.sections,
      dispatch,
      dataHover,
      closeDataPanel,
      dockDataPanel,
      detachDataPanel,
      layoutHasDock,
      dock,
      setDock,
      setDataPanelId,
    ],
  );

  /**
   * The command palette's open state.
   *
   * **Ctrl/⌘+K, and it cannot be a bare letter.** `use-type-to-search.ts` claims
   * every unmodified letter app-wide for the symbol box, and `type-to-search.ts`
   * returns `null` for anything carrying a modifier — so the two listeners are
   * incapable of fighting over a keystroke rather than merely arranged not to.
   */
  const [commandsOpen, setCommandsOpen] = useState(false);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      // Ctrl/⌘+K, and Ctrl+Q — Q for QuFolio (owner, 2026-10-10). Ctrl only for
      // Q: ⌘Q quits the browser on a Mac before any page hears it.
      const opens = (key === "k" && (event.ctrlKey || event.metaKey)) || (key === "q" && event.ctrlKey && !event.metaKey);
      if (!opens || event.altKey || event.shiftKey) return;
      // Taken from the browser as well as the page: ⌘K is a Firefox and Chrome
      // search-bar shortcut, and two search boxes opening at once is nobody's
      // shortcut — the same argument `useTypeToSearch` makes for its own key.
      event.preventDefault();
      setCommandsOpen((open) => !open);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // **The chat panel is the registry's agent reader** (`components/chat/`,
  // docs/features/chat.md): it registers `clientTools(actionContext)` with
  // quevee and runs them as the agent asks. Ask AI reaches it through the
  // opener below. The registry is also published on `window` in dev, the way
  // `__mmBuilder` is, so it can be driven from a console without a model.
  //
  // All three readers, not just the manifest: `search` is what a command palette
  // would show, `run` is what a menu item would call. `run` hands back the
  // outcome rather than throwing, because a refusal carrying its reason *is* the
  // design and a console that only ever printed a stack trace would hide it —
  // `tools` is the one that throws, since that is what the bridge expects.
  // For a control mounted far from here — the data panel's "add Bar
  // Intelligence" link — so it runs the same action the agent would.
  useEffect(() => {
    setActionRunner(
      (id, args) => actionById(id)?.run(actionContext, args) ?? { ok: false, reason: `no action called ${id}` },
    );
    return () => setActionRunner(undefined);
  }, [actionContext]);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __qfActions?: unknown }).__qfActions = {
      tools: clientTools(actionContext),
      all: ACTIONS,
      search: (query: string) => searchActions(query),
      run: (id: string, args: Record<string, unknown> = {}) =>
        actionById(id)?.run(actionContext, args) ?? { ok: false, reason: `no action called ${id}` },
    };
  }, [actionContext]);

  /**
   * Ask AI's opener (`lib/chat-open.ts`, rules in `askAiOpener`): show the AI
   * panel and send the text. Published here rather than by the panel because
   * the panel may not be mounted yet: putting it in the dock is what mounts
   * it, and it sends `aiAsk` the moment it is.
   */
  useEffect(() => {
    if (!chatOn) return;
    setChatOpener(
      askAiOpener({
        hasDock: layoutHasDock,
        dock,
        setDock,
        float: () => setAiFloating(true),
        ask: (text) => setAiAsk((prev) => ({ text, nonce: (prev?.nonce ?? 0) + 1 })),
      }),
    );
    return () => setChatOpener(undefined);
  }, [chatOn, layoutHasDock, dock, setDock]);

  // The agent's tools and the screen it is told about, read when a tool runs
  // or a turn is sent — never a copy taken when the panel mounted.
  const actionContextRef = useRef(actionContext);
  actionContextRef.current = actionContext;
  const group: ChartGroup | undefined = chartSection && chartGroupConfig(chartSection);

  /** Show the lock's toast under one chart's symbol box, for a few seconds. */
  const refuseLocked = useCallback((chartGroupId: string) => {
    if (lockNoticeTimer.current) clearTimeout(lockNoticeTimer.current);
    setLockNotice(chartGroupId);
    lockNoticeTimer.current = setTimeout(() => setLockNotice(null), LOCK_NOTICE_MS);
  }, []);
  useEffect(
    () => () => {
      if (lockNoticeTimer.current) clearTimeout(lockNoticeTimer.current);
    },
    [],
  );

  /**
   * "Chart this" — a screener row, the journal, a Community post, the data
   * panel's peers. Through `routePick`, so a locked active chart falls through to
   * the next unlocked one (decision 2, owner 2026-10-06), and the toast only
   * when every chart on the layout is locked. The reducer refuses a locked chart
   * on its own; this is the half that knows where else to send the pick.
   */
  const chartSymbol = useCallback(
    (preferredChartGroupId: string, symbol: string): SymbolRoute => {
      const route = routePick(workspace, layout, preferredChartGroupId);
      if (route.kind === "chart") {
        dispatch({ type: "setSymbol", chartGroupId: route.chartGroupId, symbol });
      } else {
        refuseLocked(route.chartGroupId);
      }
      return route;
    },
    [workspace, layout, dispatch, refuseLocked],
  );
  chartSymbolRef.current = chartSymbol;

  /**
   * The calendar's symbol click (owner, 2026-11-01): switch to a layout that
   * holds a chart and chart the symbol there. `chartSymbol`'s rule, aimed at
   * *that* layout rather than the one on screen — its active chart (the one
   * last clicked, else its first) through `routePick`, so a locked chart falls
   * through to the next unlocked one and every chart locked is the toast.
   */
  const chartJump = useMemo<ChartJump>(
    () => ({
      targets: chartLayoutTargets(workspace.layouts),
      open: (layoutId, symbol) => {
        const at = workspace.layouts.findIndex((l) => l.id === layoutId);
        const target = workspace.layouts[at];
        const section = activeChartSection(target?.sections, activeSectionId);
        const preferred = section ? chartGroupConfig(section) : undefined;
        if (!target || !section || !preferred) return;
        const route = routePick(workspace, target, preferred.id);
        if (route.kind === "chart") {
          dispatch({ type: "setSymbol", chartGroupId: route.chartGroupId, symbol });
          const routed = target.sections.find((sec) => chartGroupConfig(sec)?.id === route.chartGroupId);
          if (routed) setActiveSectionId(routed.id);
        } else {
          refuseLocked(route.chartGroupId);
        }
        setLayoutIndex(at);
      },
    }),
    [workspace, activeSectionId, dispatch, refuseLocked, setLayoutIndex],
  );

  /**
   * Open the symbol search for one chart — a click on its box, or a letter typed
   * at it. A locked chart refuses and shows the toast; the keystroke is not
   * taken into a search (the plan's mock). Answers whether the search opened.
   */
  const openSearchFor = useCallback(
    (chartGroupId: string, query: string, select: boolean, keeps: string, replace: boolean) => {
      if (routeTyped(workspace, chartGroupId).kind === "locked") {
        refuseLocked(chartGroupId);
        return false;
      }
      const next = { chartGroupId, query, select, keeps };
      // A box click replaces an open dialog (it names its box); a letter typed
      // at the page must not open a second one.
      setSearch((open) => (replace ? next : (open ?? next)));
      return true;
    },
    [workspace, refuseLocked],
  );

  /**
   * A letter typed with focus nowhere opens the search on the selected chart.
   *
   * Answers whether it took the keystroke. A layout with no chart in it refuses
   * — there is nothing to chart into, and swallowing letters typed at a
   * screener-only screen would be a dead keyboard. An open dialog accepts and
   * ignores, because a stray letter at the page must not open a second one.
   */
  useTypeToSearch((letter) => {
    if (!group) return false;
    // Taken either way: a locked chart answers with its toast, which is acting
    // on the letter — handing it back would let the browser's find-as-you-type
    // have it.
    openSearchFor(group.id, letter, false, group.symbol, false);
    return true;
  });


  // The journal, fetched once here rather than per chart: it is one document,
  // and every chart on a layout asking for it separately would be the same
  // request three times.
  useEffect(() => {
    if (!journalEnabled()) return;
    let cancelled = false;
    fetchJournal()
      .then((loaded) => {
        if (cancelled) return;
        setJournal({
          plans: loaded.plans ?? [],
          trades: loaded.trades ?? [],
          executions: loaded.executions ?? [],
        });
      })
      // No journal rather than a broken app. It is an overlay on the chart and
      // a panel of its own; neither is the data the app exists to show.
      .catch(() => {
        if (!cancelled) setJournal({ plans: [], trades: [], executions: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [journalStamp]);

  // Derived, not stored: the same reasoning as the levels themselves. A stored
  // copy would be a second answer that a corrected fill could not reach.
  const journalLines = useMemo(
    () =>
      group?.symbol
        ? journalAnnotationsFor(
            journal.plans,
            journal.trades,
            journal.executions,
            group.symbol,
            barTimes.get(group.symbol.toUpperCase()),
          )
        : { annotations: [], editable: new Set<string>() },
    [journal, group?.symbol, barTimes],
  );

  /**
   * Whether the screener is on screen at all.
   *
   * View state, for the same reason the editor's open state is (D8): hiding a
   * panel is not a layout change, and persisting it would mean a workspace saved
   * with the screener hidden comes back with a column missing that the user
   * never deleted. `withoutScreener` therefore rewrites the layout *on the way
   * to the grid* and nothing writes back.
   *
   * **Per layout, because hiding is about one arrangement and not about the
   * app.** A single boolean here meant hiding the screener on a chart-and-
   * screener layout also emptied the column on every other layout that had one,
   * and revealing it on either brought back both. A layout is where the screener
   * *lives* — one may not even have one — so which of them is showing is a fact
   * about each, not one answer shared between them.
   *
   * Held as the set of layouts it is **hidden** on rather than shown, so the
   * default is showing without every layout having to be enumerated first — a
   * layout created a moment ago is not in the set and is therefore on screen,
   * which is what `useState(true)` used to say for all of them at once.
   */
  /**
   * The legend asked to open the indicator editor at one family.
   *
   * Nonce-carrying, like the toolbar's `goTo` date: clicking the same row twice
   * has to mean twice, or reopening after a close would be silent. Session
   * state (D8) — nothing about which editor is showing belongs in a workspace.
   */
  const [editRequest, setEditRequest] = useState<{ key: string; nonce: number }>();

  /**
   * What the value legend's rows can do, for one chart.
   *
   * **The family grouping is `appliedIndicators`', not a second one.** It keys
   * by pane, catalog entry and parameters — so CE's six plots are one family
   * while `sma_50` and `sma_200` stay two — and the picker's own `editing` state
   * is that same key, which is what lets a legend row open the editor at the
   * right row rather than at the top of the list.
   *
   * Show/hide addresses the **plot**; remove and edit address the **family**,
   * which is why only its first plot in draw order carries them. Removing one of
   * CE's six leaves an indicator missing five sixths of itself, and its
   * parameters are the family's — the alias encodes them.
   *
   * Rebuilt per render rather than memoised: it is a handful of map lookups over
   * the panes already in hand, and the alternative is a dependency list that has
   * to name every plot's `hidden` to stay correct.
   */
  const legendControlsFor = useCallback(
    (target: ChartGroup): LegendControls => {
      const families = appliedIndicators(target, cachedCatalog());
      const familyOf = new Map<string, (typeof families)[number]>();
      const names = new Map<string, string>();
      for (const family of families) {
        // **One plot, or several?** That decides which of the picker's two names
        // a row wants. A family drawing one line has its parameters *in* the
        // family label and nothing else to say — "SMA 50", not "SMA" — while one
        // drawing several needs them told apart, and there the output label is
        // the distinguishing half: "MACD", "Signal", "Histogram", or
        // Chandelier's "Long Stop" and "Short Stop". Repeating the parameters on
        // each of those would be the same three numbers three times.
        //
        // Fills are excluded from the count and not from the naming: a band is
        // an extra *drawing* of a series already listed, so counting it would
        // push a single-line indicator onto the multi-plot branch and drop its
        // parameters for no reason the reader can see.
        const drawn = family.plots.filter((plot) => plot.type !== "band");
        for (const plot of family.plots) {
          familyOf.set(plot.plotId, family);
          names.set(plot.plotId, drawn.length > 1 ? plot.label : family.label);
        }
      }
      return {
        nameOf: (plotId) => names.get(plotId),
        // The **family**, not a chosen head: which of its plots leads is the
        // legend's, because only it knows which rows survived its own skips.
        familyOf: (plotId) => {
          const family = familyOf.get(plotId);
          return family
            ? { key: family.key, label: family.label, removable: family.removable }
            : undefined;
        },
        onToggle: (plotId, hidden) => dispatch({ type: "setPlotHidden", plotId, hidden }),
        // Every plot the family owns, fills included — they draw no legend row,
        // so the heading is the only control that can reach them.
        onToggleFamily: (plotId, hidden) => {
          const family = familyOf.get(plotId);
          if (!family) return;
          dispatch({
            type: "setPlotsHidden",
            plotIds: family.plots.map((plot) => plot.plotId),
            hidden,
          });
        },
        // `targets` folded left, exactly as the picker's × does it: an own-pane
        // indicator is removed by its pane and an overlay by its aliases, and
        // that difference is the reason the field exists.
        onRemove: (plotId) => {
          const family = familyOf.get(plotId);
          if (!family) return;
          dispatch({
            type: "setPanes",
            chartGroupId: target.id,
            panes: family.targets.reduce(removeIndicator, target).panes,
          });
        },
        onEdit: (plotId) => {
          const family = familyOf.get(plotId);
          if (family) setEditRequest({ key: family.key, nonce: Date.now() });
        },
      };
    },
    [dispatch],
  );

  /**
   * Which company events each chart marks along the foot of its price pane.
   *
   * **Saved per chart** (owner, 2026-10-08): `ChartGroup.showEarnings` and
   * `showNews`, written through `setChartEvents`. They were view state here until
   * then, which turned every chart's markers back on at each reload. Absent
   * means on — the data is fetched only when a chart asks, and a feature nobody
   * can see is one nobody will find.
   */
  const eventsFor = useCallback(
    (chartGroupId: string) => {
      const group = findChartGroup(workspace, chartGroupId);
      return { earnings: group?.showEarnings ?? true, news: group?.showNews ?? true };
    },
    [workspace],
  );

  /**
   * Whether the dock's track is on screen: false only for a dock with no panels.
   *
   * Was view state (`hiddenScreeners`); the dock made it workspace data, so a
   * hidden screener stays hidden across a reload. A layout with no screener
   * section has no dock to hide and reads as shown, which is what
   * `withoutScreener` has always needed.
   */
  const screenerShown = !!layout && (!layoutHasDock || dock.panels.length > 0);
  const screenerInDock = layoutHasDock && isShowing(dock, "screener");

  /**
   * The chart section put full screen, or null.
   *
   * View state (D8), never saved: a reload opens the layout as it was. Two
   * halves, and both are needed — the browser's own full screen hides its
   * chrome and the taskbar, and `onlySection` is what makes the one chart fill
   * that screen. The *document* goes full screen, never the chart's element:
   * the symbol dialog, the menus and the indicator picker all render outside
   * the chart, and an element in full screen shows nothing but its own subtree.
   *
   * Escape is the browser's, so it arrives as a `fullscreenchange` with no
   * element; that is the one exit path both halves hear about.
   */
  const [fullscreenId, setFullscreenId] = useState<string | null>(null);
  const fullscreenSection =
    fullscreenId && layout?.sections.some((s) => s.id === fullscreenId) ? fullscreenId : null;
  const toggleFullscreen = (sectionId: string) => {
    if (fullscreenSection === sectionId) {
      setFullscreenId(null);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
      return;
    }
    setActiveSectionId(sectionId);
    setFullscreenId(sectionId);
    // Refused (an iframe, a browser setting) is survivable: the chart still
    // fills the window, which is most of what was asked for.
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen?.().catch(() => {});
    }
  };
  /**
   * ‹ › in full screen: the next chart in Z order, wrapping. Moves the full
   * screen and the active ring together, and leaves the browser's full screen
   * alone — the document is what is full screen, not a chart.
   */
  const fullscreenOrder = layout && fullscreenSection ? chartCycleOrder(layout) : [];
  const stepFullscreen = (step: 1 | -1) => {
    if (!layout || !fullscreenSection) return;
    const next = cycleChart(layout, fullscreenSection, step);
    if (!next) return;
    setActiveSectionId(next);
    setFullscreenId(next);
  };
  const stepFullscreenRef = useRef(stepFullscreen);
  stepFullscreenRef.current = stepFullscreen;
  // Alt+←/→, only while a chart is full screen. Plain arrows are the chart's
  // and every input's, so they are left alone.
  useEffect(() => {
    if (!fullscreenSection) return;
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      e.preventDefault();
      stepFullscreenRef.current(e.key === "ArrowRight" ? 1 : -1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreenSection]);

  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) setFullscreenId(null);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const shownLayout =
    layout &&
    (fullscreenSection
      ? onlySection(layout, fullscreenSection)
      : screenerShown
        ? layout
        : withoutScreener(layout));

  // The editor edits the first screener section; the strip anchors to an edge of
  // the chart region, on the side the screener is docked.
  const screenerSection = shownLayout?.sections.find((s) => s.kind === "screener");

  /**
   * The screener as **saved**, which is not the same question.
   *
   * `shownLayout` has the screener spliced out while it is hidden, so anything
   * derived from it disappears with the panel. That is right for the grid and
   * wrong for the saved-filter button: a filter is workspace data and stays
   * applied while the panel is away, so hiding the screener took away the only
   * control that could clear it. The filter was still on, still narrowing the
   * rows, and unreachable.
   *
   * `hasScreener` already reads the saved layout for the same reason — the
   * show/hide button cannot live in the thing it un-hides.
   */
  const savedScreenerSection = layout?.sections.find((s) => s.kind === "screener");
  const savedScreener = savedScreenerSection && screenerConfig(savedScreenerSection);
  const storedScreener = screenerSection && screenerConfig(screenerSection);
  /**
   * The editor's screener and the conditions popover's, with their **links**
   * resolved (`lib/screen-links.ts`): a linked column wears its source's
   * header and format, a linked condition its source's name and 🔒. Patches go
   * back through `updateScreener`, which reduces them to links again. One
   * cache serves these and `ScreenerView`, so Persons is fetched once.
   */
  const linkLoad = useMemo(() => screenLoader(publishTarget), [publishTarget]);
  const { config: screener, links: screenLinks } = useScreenLinks({
    config: storedScreener,
    screens,
    load: linkLoad,
  });
  const { config: savedScreenerShown, links: savedScreenLinks } = useScreenLinks({
    config: savedScreener,
    screens,
    load: linkLoad,
  });
  screenerRef.current = screener ? screener.screenRef : undefined;
  // The agent's Link conditions reads what the Conditions tab's picker reads.
  screenerLinksRef.current =
    screener && screenerSection
      ? {
          sectionId: screenerSection.id,
          screenRef: screener.screenRef,
          screenName: screens.find((s) => s.key === screener.screenRef)?.name,
          saved: screener.savedFilters ?? [],
          links: linksOffered(screenLinks, screens, screener.screenRef, publishTarget),
        }
      : undefined;
  // With a Strat list alone on screen the docked editor edits the columns the
  // grid shows — the Strat list — and its first edit saves them (owner, 2026-10-27).
  const stratOnScreen = useSyncExternalStore(subscribeStrategyOnScreen, strategyOnScreen);
  const builderScreener = useMemo(
    () => (screener && stratOnScreen ? { ...screener, columns: strategyColumns(screener.columns) } : screener),
    [screener, stratOnScreen],
  );
  // Whether a screener exists to be shown, which is not the same as whether one
  // is showing — the toggle has to survive the panel being hidden.
  const hasScreener = layout?.sections.some((s) => s.kind === "screener") ?? false;

  /**
   * A screener for a layout without one, on `placement` — the strip's Add
   * screener (right) and the layout panel's Screener row (any side). Seeded from
   * the workspace, so it is the host's to do rather than either control's.
   */
  const addScreenerAt = (placement: ScreenerPlacement) => {
    if (!layout) return;
    const added = addScreener(layout, newScreenerSection(screenerSeed(workspace)), placement);
    dispatch({
      type: "replaceLayout",
      // A dock left over from a removed screener may be hidden or data-only;
      // adding one means seeing it.
      layout: layout.dock ? { ...added, dock: openPanel(layout.dock, "screener") } : added,
    });
  };

  /**
   * A new screen of your own, and the panel moved onto it — the editor strip's
   * ＋ and the screener bar's ＋ New are one act, so they are one function.
   */
  /**
   * The screener builder (owner, 2026-10-26): ＋ New screen and ✎ Edit this
   * screen both open it, seeded differently. View state (D8) — a reopened
   * workspace does not reopen a dialog.
   */
  const [builder, setBuilder] = useState<"new" | "edit" | null>(null);
  const addNewScreen = () => setBuilder("new");

  /** Create the screen the builder drew — or a blank one when given nothing. */
  const createScreen = (built?: BuilderResult) => {
    if (!screener || !screenerSection) return;
    // **The minimum, not a copy of the screen you are
    // on.** The layout panel seeds a new *chart* from
    // the one beside it, and that instinct is wrong
    // here: a screen someone is working in carries
    // ninety-odd columns, so duplicating it hands them a
    // wall to dismantle rather than a screen to build.
    // `DEFAULT_COLUMNS` is the set a screener minted
    // from nothing already shows — the symbol, its last
    // price and the day's move — and its own docstring
    // makes this argument about the server sending every
    // column whether or not anyone asked.
    //
    // The universe travels, though: a new screen over
    // the group you are already looking at is right,
    // where a new screen over a hundred columns is not.
    //
    // **So do the named conditions, and none of them is
    // applied.** A library is what you have written, not
    // what this screen is showing — starting a screen
    // with an empty conditions popup means retyping
    // them, where carrying them costs nothing until one
    // is ticked. Sent explicitly rather than left to the
    // fallback so the new screen owns a copy from the
    // first moment and a later edit here cannot reach
    // back into it.
    void addScreen({
      name: built?.name ?? nextFreeName(screens),
      groupCodes: screener.group ? [screener.group] : [],
      columns: built?.patch.columns ?? [...DEFAULT_COLUMNS],
      // Minus a group's sealed ones: each names its own
      // screen's rule by id, and a screen of yours has
      // no such rule. Linked ones travel as links — they
      // carry no rule (`libraryForAnotherScreen`).
      savedFilters:
        built?.patch.savedFilters ?? libraryForAnotherScreen(storedScreener?.savedFilters ?? []),
      ...(built ? { filter: built.patch.filter, sort: built.patch.sort } : {}),
    })
      // **Point at it *and* fill from it.** Patching
      // `screenRef` alone moved the tab and left the
      // panel showing the columns of the screen you
      // were on — so a new screen looked exactly like
      // the one it was supposed to be a fresh start
      // from, and the whole point of a minimal default
      // set was invisible. Selecting a tab has always
      // gone through `applyScreenTo`; creating one is
      // the same act and now takes the same path.
      .then((made) =>
        applyScreenTo({
          key: made.key,
          config: storedScreener ?? screener,
          load: fetchPreset,
          onConfigChange: (patch) =>
            dispatch({
              type: "updateScreener",
              sectionId: screenerSection.id,
              patch,
            }),
        }),
      )
      // What is ticked is the panel's, not the screen row's, so it lands after
      // the fill — one more patch, the selection the builder showed.
      .then(() => {
        if (built)
          dispatch({
            type: "updateScreener",
            sectionId: screenerSection.id,
            patch: {
              filter: built.patch.filter,
              activeFilterRefs: built.patch.activeFilterRefs,
              activeFilterRef: built.patch.activeFilterRef,
            },
          });
      })
      .catch(() => {});
  };

  /**
   * The Community panel, once — the dock renders it in its half, and a layout
   * with no dock floats it. Attachments come from what is on screen: the active
   * chart, this layout, and the screen on the screener (as a copy).
   */
  const communityPanel = communityOn ? (
    <CommunityPanel
      attach={{
        chart: group,
        layout,
        screen: savedScreener
          ? {
              name:
                screens.find((s) => s.key === savedScreener.screenRef)?.name ??
                defaultScreen(screens)?.name ??
                "Screen",
              config: JSON.parse(JSON.stringify(savedScreener)) as Record<string, unknown>,
            }
          : undefined,
      }}
      shareNonce={shareNonce}
      onOpenSymbol={(symbol) => {
        // "Chart this", so a locked active chart falls through (`chartSymbol`).
        if (group) chartSymbol(group.id, symbol);
      }}
      onOpenLayout={(definition, name) => {
        // A new tab, never a replaced chart: the reader's own setup stays.
        dispatch({ type: "addLayout", layout: adoptLayout(definition, name) });
        setLayoutIndex(workspace.layouts.length);
      }}
      onClose={
        layoutHasDock
          ? () => setDock(closePanel(dock, "community"))
          : () => setCommunityFloating(false)
      }
    />
  ) : null;

  /** The AI panel, once — docked in its half, or floated with no dock. */
  const aiPanel = chatOn ? (
    <ChatPanel
      tools={() => clientTools(actionContextRef.current)}
      toolTitle={(name) => actionById(name)?.title}
      context={() => workspaceContext(layout, actionContextRef.current.activeChartGroupId)}
      ask={aiAsk}
      onClose={layoutHasDock ? () => setDock(closePanel(dock, "ai")) : () => setAiFloating(false)}
    />
  ) : null;
  /**
   * Where the strip hangs: measured on the layout as *shown*, but told the side
   * by the layout as *saved*.
   *
   * A hidden screener is on no side at all, so deriving the side from
   * `shownLayout` would send the strip back to the chart region's right edge the
   * moment a left-, top- or bottom-docked screener was hidden — and back again
   * when it returned. The columns and rows still have to come from the shown
   * layout, which is the one whose tracks the strip is positioned over.
   */
  const anchor =
    layout && shownLayout ? toolbarAnchor(shownLayout, screenerPlacement(layout)) : undefined;

  /**
   * The data panel is being **kept** rather than peeked at — the click, not the
   * hover. `dataHover.pinned` is the state; this adds "and it has a symbol",
   * which is the other half of anything being on screen.
   */
  const dataKept = !!dataPanelSymbol && (dataHover.pinned || dataDocked);

  /**
   * ...and it is holding the screener's slot, which is where a kept panel goes.
   *
   * Four conditions, and each one is a state where the slot is not there to be
   * taken: no screener in the layout, the screener hidden (the track is
   * collapsed — `withoutScreener` — so the panel would render into nothing), the
   * panel in its own window, or nothing kept open at all.
   *
   * When it cannot have the slot, a kept panel falls back to the floating dialog
   * it always used. That is not a leftover: a layout with no screener has
   * nowhere to put this, and over a chart beats nowhere.
   */
  const dataInSlot = dataDocked && !detached && screenerShown && !!screenerSection;

  // The price plot the chart-type toolbar targets. Shared with the sync fan-out,
  // which has to find the matching plot on every other chart in the layout.
  const priceSeries = group && pricePlot(group);

  /**
   * Right-click anywhere on the workspace surface opens the menu.
   *
   * `preventDefault` suppresses the browser's own menu, which would otherwise
   * cover ours. Handled at this level rather than per section so the gesture
   * works over a chart, a screener and the empty grid alike.
   */
  const openMenu = (e: ReactMouseEvent) => {
    e.preventDefault();
    // A right-click that did not land on a level clears it, or the previous
    // level's items would reappear over empty chart.
    setMenuLevel(null);
    setMenuAt({ x: e.clientX, y: e.clientY });
  };

  /**
   * Add a target to a plan, or take one away.
   *
   * The whole plan goes back rather than a patch: `savePlan` upserts a
   * `Plan`, and `normalise_shares` rescales the shares server-side so the
   * remaining targets still sum to one. Deleting a target that held a third of
   * the position and leaving the others at their old shares would quietly send
   * a third of it to the stop with no exit.
   */
  const editTargets = (planId: string, edit: (plan: Plan) => Plan["targets"]) => {
    const plan = journal.plans.find((p) => p.id === planId);
    if (!plan) return;
    void savePlan({ ...plan, targets: edit(plan) })
      .then(() => setJournalStamp((n) => n + 1))
      .catch(() => setJournalStamp((n) => n + 1));
  };

  /**
   * Offer the tour once, to a browser that has not seen it.
   *
   * **Deferred, not run on mount.** Every step points at a control that only
   * exists once the workspace has painted — the toolbar is per chart and the
   * screener mounts on its own socket — so a tour started synchronously would
   * resolve two steps and skip the rest. `shouldAutoStart` re-checks each time,
   * and gives up after a few tries rather than polling forever: a workspace
   * that has not painted in three seconds has something else wrong with it.
   */
  useEffect(() => {
    if (tourOpen) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      // The last four ticks stop insisting on a screener step: by then it is
      // not slow, it is absent — hidden, or switched off for this brand.
      if (shouldAutoStart(tries > 20)) {
        setTourOpen(true);
        window.clearInterval(timer);
      } else if (tries >= 24) {
        // Twelve seconds. Long, deliberately: `shouldAutoStart` now waits for
        // two stops past the chrome, which means the charts have painted, and
        // a cold Fly machine can take several seconds to answer the workspace
        // load. Giving up at three seconds would have shown no tour at all to
        // exactly the people on the slowest connections.
        window.clearInterval(timer);
      }
    }, 500);
    return () => window.clearInterval(timer);
    // Once per mount. `tourOpen` is read above only to bail out early; adding
    // it to the deps would restart the poll every time the tour closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ViewDetachContext.Provider value={viewDetach}>
      {tourOpen && <Tour onClose={() => setTourOpen(false)} />}
      {/* The top row of the app — there is no title header above it. What one
          said was "QuFolio" and then the symbol, interval and pane count the
          chart's own toolbar states a few pixels lower. */}
      {/* Gone in full screen: the chart is the whole screen, and the tab strip
          is a way to leave it for another layout. */}
      {!fullscreenSection && (
      <LayoutBar
        layouts={workspace.layouts}
        layoutIndex={index}
        saveStatus={saveStatus}
        onOpenSettings={() => setThemeEditorOpen(true)}
        onOpenAdmin={() => setAdminOpen(true)}
        onOpenDataHealth={() => setDataHealthOpen(true)}
        onOpenPlan={() => setPlanOpen(true)}
        onOpenGroupRequest={() => setGroupRequestOpen(true)}
        onOpenBooks={() => setBooksOpen(true)}
        // **An explicit replay clears the flag first.** Finishing records the
        // tour as seen, so without this a user who asked for it while the app
        // was still loading — and got a short tour of whatever had painted —
        // would have spent the one automatic showing on it.
        onStartTour={() => {
          resetTourSeen();
          setTourOpen(true);
        }}
        onSelect={setLayoutIndex}
        onRename={(layoutId, name) => dispatch({ type: "renameLayout", layoutId, name })}
        // The panel computes the rearranged layout and hands it over whole, so
        // no id is minted inside the reducer.
        onReplace={(next) => dispatch({ type: "replaceLayout", layout: next })}
        onAddScreener={addScreenerAt}
        onAdd={(next) => {
          dispatch({ type: "addLayout", layout: next });
          // Appended, so its index is the length before the append. Selected
          // here rather than in the panel because selection is this
          // component's state — the panel has no way to know where it landed.
          setLayoutIndex(workspace.layouts.length);
        }}
        // **The selection is this component's, so the guard is too.** Hiding
        // reshapes nothing and only moves the selection when it is the layout
        // on screen; deleting shifts every later index down by one, which
        // silently retargets the selection to a different desk if nobody
        // adjusts it. Both answers are computed from the array *before* the
        // dispatch, which is the only one that still has the layout in it.
        onSetLayoutHidden={(layoutId, hidden) => {
          dispatch({ type: "setLayoutHidden", layoutId, hidden });
          if (hidden) {
            setLayoutIndex(selectionAfterHide(workspace.layouts, layoutId, index));
          }
        }}
        onSetSymbolLocked={(layoutId, locked) => {
          dispatch({ type: "setLayoutSymbolLocked", layoutId, locked });
          if (!locked) setLockNotice(null);
        }}
        onRemoveLayout={(layoutId) => {
          setLayoutIndex(selectionAfterRemove(workspace.layouts, layoutId, index));
          dispatch({ type: "removeLayout", layoutId });
        }}
        // The selected chart is the one a newly-ticked link harmonises *from*,
        // which is the same chart the toolbar, the ring and the screener's row
        // cursor already drive. Passed even when nothing is selected: the
        // reducer falls back to the layout's first chart rather than guessing.
        onSync={(layoutId, sync) =>
          dispatch({ type: "setSync", layoutId, sync, fromChartGroupId: group?.id })
        }
        showTrades={showTrades}
        // Offered only with a journal to show. A toggle that reveals an empty
        // panel because no server is configured is worse than no toggle — and
        // `available("journal")` is the second reason for the same rule: a
        // brand without a journal, or an account without the capability.
        onShowTrades={
          available("journal") && journalEnabled() ? setShowTrades : undefined
        }
        // Where the journal sits and whether it is put away are workspace data,
        // so both come from the workspace and go back through the reducer —
        // unlike `showTrades` above, which is only which tab is selected now.
        journalIndex={workspace.journalIndex}
        journalHidden={workspace.journalHidden}
        soleLayoutId={soleLayoutId}
        elsewhere={elsewhere}
        onDetachLayout={onDetachLayout}
        onFocusElsewhere={onFocusElsewhere}
        currentNote={dataNote}
        onMoveTab={(from, to) => {
          // **A reorder retargets the selection**, for the reason a delete
          // does: `layoutIndex` is a position in `workspace.layouts`, and
          // moving a tab renumbers every layout it passed. Computed from the
          // array *before* the dispatch, which is the only one that still lines
          // the old index up with a layout.
          const selected = workspace.layouts[layoutIndex]?.id;
          const moved = moveTabOrder(workspace.layouts, workspace.journalIndex, from, to);
          const next = moved.layouts.findIndex((l) => l.id === selected);
          if (next >= 0 && next !== layoutIndex) setLayoutIndex(next);
          dispatch({ type: "moveTab", from, to });
        }}
        onSetJournalHidden={(hidden) => {
          // Hiding the journal while you are looking at it has to move the
          // selection, for `onSetLayoutHidden`'s reason: a tab that is gone from
          // the strip cannot be the one lit up in it.
          if (hidden && showTrades) setShowTrades(false);
          dispatch({ type: "setJournalHidden", hidden });
        }}
        // The same two facts the screener's Publish reads, so an admin who can
        // publish a screen can publish a desk and nobody else sees either.
        publishTarget={publishTarget}
        onPublishLayout={publishTarget ? publishToClubLayout : undefined}
        publishTargetName={publishTargetName}
        onPublishedChanged={refreshLayoutPresets}
        onUnpublishLayout={publishTarget ? unpublishLayout : undefined}
        // Offered to everyone with a server to ask — a saved layout is yours,
        // needs no club and no grant, which is the whole point of the `user`
        // owner kind.
        onSaveLayoutTemplate={layoutsConfigured ? saveOwnTemplate : undefined}
        onRemoveLayoutTemplate={layoutsConfigured ? removeOwnTemplate : undefined}
        layoutPresets={layoutPresets}
        onApplyLayoutPreset={applyLayoutPreset}
        inheritedLayouts={inheritedLayouts}
        sharedStates={sharedStates}
        // Promote & Publish: never a virtual group (only the platform admin
        // authors those, and from ☰ › Author as).
        promoteTo={groupAccounts.filter((g) => !virtualGroups[g.group])}
        onPromoteLayout={(account, layout, name) => promoteLayout({ account, layout, name })}
        onAddSection={(layoutId, kind) => dispatch({ type: "addMacroSection", layoutId, kind })}
        onOpenPanel={() => void refreshLayoutPresets()}
        onOpenSearch={() => setCommandsOpen(true)}
      />
      )}

      {/* The one-time question about an unlinked copy of a group layout —
          one at a time, and only once the saved workspace is in. */}
      {/* A new version clicked in Shared with me — the member's to take. */}
      {sharedUpdate && (
        <SharedUpdateDialog
          name={sharedUpdate.mine.name}
          version={sharedUpdate.preset.version ?? 0}
          publishedDay={publishedDay(sharedUpdate.preset.publishedAt)}
          mine={sharedUpdate.mine.version}
          onAnswer={(choice) => void answerSharedUpdate(choice)}
        />
      )}

      {legacyLayoutPrompt && (
        <LegacyLayoutDialog
          groupName={legacyLayoutPrompt.groupName}
          layoutName={legacyLayoutPrompt.preset.name}
          onAnswer={(choice) => void answerLegacyLayout(choice)}
        />
      )}

      {/* **Nothing is charted until the load answers**, and the empty branch
          below is what stands in.

          `usePersistentWorkspace` seeds the reducer with the demo workspace so
          the app is usable before the first save, but on an account that *has*
          saved, that fixture is a decoy: it mounts a full chart tree, boots a
          WebGL surface and puts a bar request on the wire for a symbol nobody
          asked for. Every id in it is freshly minted, so when `reset` lands
          none of them match — `SectionGrid` keys sections by `section.id` and
          `structuralKey` opens with `group.id` — and the whole grid unmounts,
          tears the surface down and rebuilds it. The layout either side is
          identical, so what that costs is a blink with no cause on screen.

          Only `loading` is gated. `error` deliberately still renders: a load
          that failed leaves the demo workspace as the only thing there is, and
          writes are already refused (see `blocked` in `api/persistence`). With
          no backend the status is `off` and this changes nothing. */}
      {layout && shownLayout && saveStatus !== "loading" ? (
        showTrades ? (
          <TradesView
            plans={journal.plans}
            trades={journal.trades}
            executions={journal.executions}
            prices={prices}
            active={tradeGroup}
            onPick={setTradeGroup}
            // Charting from the list is the reason to have it beside the
            // charts. Picking a symbol puts it on the active chart and closes
            // the journal, because the next thing you want is to look at it.
            onSelectSymbol={
              group
                ? (symbol) => {
                    // The journal's "chart this": as a screener click.
                    chartSymbol(group.id, symbol);
                    setShowTrades(false);
                  }
                : undefined
            }
            onEdit={(plan) => {
              setEditingPlan(plan);
              setPlanningSymbol(plan.symbol);
            }}
          />
        ) : (
        <SectionGrid
          layout={shownLayout}
          // Rendered inside the chart's own column rather than above the whole
          // grid: symbol and frequency describe one section, and spanning them
          // across everything put them over the screener while costing every
          // section a row of height.
          activeSectionId={chartSection?.id}
          onActivate={setActiveSectionId}
          // The armed tool goes to the active chart and nowhere else. One
          // palette serves whichever chart has the ring, so handing the tool to
          // every chart would let a trendline armed here be drawn on any of them.
          drawing={
            group
              ? { chartGroupId: group.id, tool: armedTool, trading: armedTrading }
              : undefined
          }
          journal={
            group
              ? {
                  chartGroupId: group.id,
                  annotations: journalLines.annotations,
                  editable: journalLines.editable,
                }
              : undefined
          }
          // **A dragged stop is an amendment, not an edit.** The line moved on
          // screen; this writes the number it was drawn from, and the reload
          // below is what makes that position true. Attribution is the whole
          // point of the amendment table — the server defaults `source` to
          // `user`, which a drag is.
          onLevelMenu={(annotationId, x, y) => {
            const level = parseLevelId(annotationId);
            if (!level) return;
            setMenuLevel(level);
            setMenuAt({ x, y });
          }}
          legendControlsFor={legendControlsFor}
          // Delete over a selected drawing. Only the user's own reach here —
          // the builder filters derived ones out, because a plan level is not
          // the user's to delete and the next render would put it back.
          // **A drawn plan becomes a journal row, not a drawing.** The drag
          // gives the two numbers that define 1R; everything else is the same
          // arithmetic the form does, so a plan drawn on the chart and one
          // typed into the form are the same object by the time they are saved.
          onPlanDrawn={(_chartGroupId, entry, stop, at) => {
            const symbol = group?.symbol;
            if (!symbol || !planAccountId || entry === stop) return;
            // Direction comes from the drag: pull the stop *below* the entry and
            // it is a long. Nothing else has to be asked, which is the point of
            // drawing it rather than filling a form.
            const direction = stop < entry ? "long" : "short";
            // **Rounded first, then measured.** Deriving R from the raw drag and
            // storing rounded levels made T1 a cent off its own 2R — the plan
            // disagreeing with itself about the only number that matters.
            const round2 = (v: number) => Math.round(v * 100) / 100;
            const entryAt = round2(entry);
            const stopAt = round2(stop);
            const perShare = Math.abs(entryAt - stopAt);
            const step = direction === "long" ? perShare : -perShare;
            void savePlan({
              id: mintUlid(),
              accountId: planAccountId,
              symbol,
              direction,
              entry: entryAt,
              entryOrder: "limit",
              // **Where it was drawn, so the levels ray from there.** A plan's
              // `createdAt` is a moment during the session and the daily bars
              // are stamped midnight, so `createdAt` alone starts the ray past
              // the end of the data — which fell back to spanning the chart and
              // read as a line drawn leftwards.
              validFrom: new Date(at * 1000).toISOString(),
              stop: stopAt,
              // The account's default risk, matching the form's seed — the one
              // number a drag cannot express and the one a plan cannot omit.
              risk: 500,
              status: "armed",
              // T1 and T2, the same 2R and 3R the form seeds. Drawn plans and
              // typed ones must not differ, or "draw it" would quietly mean
              // something else.
              targets: [
                { id: mintUlid(), price: round2(entryAt + step * 2), share: 1 },
                { id: mintUlid(), price: round2(entryAt + step * 3), share: 1 },
              ],
            })
              .then(() => setJournalStamp((n) => n + 1))
              .catch(() => setJournalStamp((n) => n + 1));
            // Disarmed after one plan, like every other tool: the next drag on
            // the chart should pan it, not open a second position.
            setArmedTool(null);
          }}
          clearNonce={clearNonce}
          // A patch of one drawing's style. No pane id: a drawing is selected on
          // the chart rather than in a pane, and the reducer finds it by its ULID.
          onPlacing={setPlacing}
          onAnnotationStyled={(chartGroupId, annotationId, style) =>
            dispatch({ type: "styleAnnotation", chartGroupId, annotationId, style })
          }
          onAnnotationsDeleted={(chartGroupId, annotationIds) => {
            for (const pane of group?.panes ?? []) {
              for (const annotationId of annotationIds) {
                dispatch({ type: "removeAnnotation", chartGroupId, paneId: pane.id, annotationId });
              }
            }
          }}
          // **A dragged level is an edit before the freeze and an amendment
          // after it**, and they are different endpoints. Before the first fill
          // there is nothing to amend against, so the store refuses an
          // amendment outright — every drag on an armed plan used to come back
          // 400, the re-read below put the line back, and it read as a drag
          // that simply did not work.
          onLevelMoved={(annotationId, price) => {
            const level = parseLevelId(annotationId);
            // Not a plan level: a fill mark, or the user's own drawing. Editing
            // a plan because somebody moved a trendline is the failure this
            // guard exists for.
            if (!level) return;
            const plan = journal.plans.find((p) => p.id === level.planId);
            if (!plan) return;

            // Re-read either way: on success because the plan is the source the
            // line is drawn from, and on failure because the line is sitting
            // where the drag left it while the server refused — so the only
            // honest thing on screen is where the plan actually is.
            const reread = () => setJournalStamp((n) => n + 1);

            // `plan.planned` is the freeze, and it is the same test
            // `plan-levels` uses to decide whether the entry gets a grip at
            // all. One rule, read twice, rather than two that can disagree.
            if (plan.planned == null) {
              // The ordinary upsert — what the form and the right-click target
              // editor already use, so `normalise_shares` still rescales and
              // `revisions` still counts the walk-down.
              void savePlan(withLevel(plan, level, price)).then(reread).catch(reread);
              return;
            }

            // Past the freeze the entry is a fact and the server refuses it, so
            // no grip is offered for it — this is the belt to that braces.
            if (level.kind === "entry") return;
            void amendPlan(
              level.planId,
              level.kind === "target" ? "target" : "stop",
              price,
              level.targetId,
            )
              .then(reread)
              .catch(reread);
          }}
          onLastPrice={rememberPrice}
          onAnnotationDrawn={(chartGroupId, paneId, annotation) => {
            // Disarmed first, and before any dialog: a `prompt` blocks the
            // event loop, and a tool still armed underneath it would have the
            // click that dismisses the dialog land on the canvas as a second
            // drawing.
            setArmedTool(null);

            // **A drawing arrives with no style, and that was white on white.**
            // `resolveColorRef(undefined)` is `undefined`, so the renderer fell
            // back to SciChart's own default — which is white, and invisible on
            // every light theme. Stamped here rather than defaulted in the
            // renderer because the annotation is *stored*: a drawing made today
            // must not change colour when the default does, and a token follows
            // a theme switch where a resolved hex would not (D13).
            //
            // `text.secondary` is the same value `AnnotationStyleBar` already
            // claims an unstyled drawing resolves to, so the control opens on
            // the colour that is actually on screen.
            // A highlighted bar has no line, only its fill.
            let drawn: typeof annotation =
              annotation.type === "barHighlight"
                ? annotation
                : { ...annotation, style: { stroke: "text.secondary", ...annotation.style } };
            if (annotation.type === "text") {
              // The one kind whose content cannot come from the gesture. Asked
              // here rather than in the renderer because a browser dialog has
              // no business inside a WebGL adapter — the chart reports what was
              // drawn, and the host decides what it means.
              const text = window.prompt("Label", "");
              // Cancelled, or nothing typed: an empty NativeTextAnnotation
              // renders as zero pixels, so storing one would leave something
              // permanent that cannot be seen or selected to delete.
              if (!text?.trim()) return;
              drawn = { ...drawn, text: text.trim() };
            }
            dispatch({ type: "addAnnotation", chartGroupId, paneId, annotation: drawn });
          }}
          // One per chart, each driving its own group. A single shared toolbar
          // was the other option and is the wrong one: it can only state one
          // symbol and one interval, so on a two-chart layout it printed one
          // chart's symbol above another's candles. The ring, not the toolbar,
          // is what says which chart a screener row will chart into.
          renderDataView={(section) => (
            <DataViewCell
              config={dataViewConfig(section) ?? {}}
              inUse={keysInUse(layout ?? shownLayout, section.id)}
              symbol={followedChart?.symbol}
              chartGroupId={followedChart?.id}
              onChoose={(choice) => {
                if (!layout) return;
                const next = setCell(layout, section.id, choice, () =>
                  newChartSection(followedChart?.symbol, followedChart?.frequency),
                );
                if (next !== layout) dispatch({ type: "replaceLayout", layout: next });
              }}
              onSymbol={followedChart ? (symbol) => chartSymbol(followedChart.id, symbol) : undefined}
              follow={
                layout && isDataOnly(layout)
                  ? {
                      current: tiedTo?.id,
                      options: followOptions,
                      onFollow: (id) => dispatch({ type: "replaceLayout", layout: setFollow(layout, id) }),
                    }
                  : undefined
              }
            />
          )}
          chartHeader={(section) => {
            const own = chartGroupConfig(section);
            return own ? (
              <ChartToolbar
                chartGroupId={own.id}
                symbol={own.symbol}
                frequency={own.frequency}
                visibleBars={visibleBarsOf(own)}
                // **This** chart, not the selected one: clicking a box means
                // the box you clicked, whichever chart it belongs to.
                // A locked chart answers with its toast instead (`openSearchFor`).
                onOpenSearch={(query, select) => {
                  openSearchFor(own.id, query, select, own.symbol, true);
                }}
                // The no-symbol-service box commits typed text directly; the
                // reducer refuses a locked chart, and this says so.
                onSymbol={(symbol) => {
                  if (isSymbolLocked(own) && own.symbol !== symbol) refuseLocked(own.id);
                  else dispatch({ type: "setSymbol", chartGroupId: own.id, symbol });
                }}
                lock={{
                  locked: isSymbolLocked(own),
                  onToggle: (locked) => {
                    dispatch({ type: "setSymbolLocked", chartGroupId: own.id, locked });
                    if (!locked && lockNotice === own.id) setLockNotice(null);
                  },
                  notice: lockNotice === own.id,
                }}
                onFrequency={(frequency: Frequency) =>
                  dispatch({ type: "setFrequency", chartGroupId: own.id, frequency })
                }
                onVisibleBars={(visibleBars) =>
                  dispatch({ type: "setBarWindow", chartGroupId: own.id, visibleBars })
                }
                // Pre-filled with the chart's own symbol, which is the whole
                // reason the control lives on the toolbar rather than in a menu.
                calendar={calendar}
                onGoToDate={(date) =>
                  // A fresh nonce every time, including for the same date: this
                  // is an action, and repeating an action repeats it.
                  setGoTo((prev) => ({
                    chartGroupId: own.id,
                    date,
                    nonce: (prev?.nonce ?? 0) + 1,
                  }))
                }
                // Disabled rather than absent past two charts, with the reason
                // on hover: a control that vanishes teaches nothing, and the
                // reason is the whole content of the rule. `undefined` is what
                // the toolbar reads as "not offered here".
                pip={
                  pipOfferedOn(chartIds.length)
                    ? {
                        on: own.pipView !== undefined,
                        onToggle: () =>
                          dispatch({
                            type: "setPipView",
                            chartGroupId: own.id,
                            // Opens on `chart`, which is what a PIP is for
                            // first. The panel's own bar changes it from there.
                            view: own.pipView ? null : "chart",
                          }),
                      }
                    : undefined
                }
                train={
                  voices.length > 0 && own.symbol
                    ? { onOpen: () => setTraining({ chartGroupId: own.id, symbol: own.symbol, frequency: own.frequency }) }
                    : undefined
                }
                fullscreen={{
                  on: fullscreenSection === section.id,
                  onToggle: () => toggleFullscreen(section.id),
                  ...(fullscreenSection === section.id && fullscreenOrder.length > 1
                    ? {
                        cycle: {
                          position: fullscreenOrder.indexOf(section.id) + 1,
                          count: fullscreenOrder.length,
                          onStep: stepFullscreen,
                        },
                      }
                    : {}),
                }}
              />
            ) : undefined;
          }}
          onContextMenu={openMenu}
          goTo={goTo ?? undefined}
          onChartWindow={(chartGroupId, window) =>
            dispatch({ type: "setBarWindow", chartGroupId, ...window })
          }
          onPip={(chartGroupId, patch) => applyPip(chartGroupId, patch)}
          // The two-chart rule: the panel hosts on the chart you are *not*
          // using. Visibility only — both charts keep their subscriptions, so
          // clicking between them costs a repaint rather than a refetch.
          pipDrawn={(chartGroupId) => pipDrawnOn(chartGroupId, chartIds, chartSection && chartGroupConfig(chartSection)?.id)}
          // A one-track full-screen grid has no seams, but a size it reported
          // would be a list of one written over the saved layout's.
          onColumnSizes={(columnSizes) =>
            !fullscreenSection &&
            dispatch({
              type: "setColumnSizes",
              layoutId: layout.id,
              // A drag made while the screener is hidden moves seams the saved
              // layout still has a column between, so the hidden column's width
              // is spliced back before the write. Saving the short list would
              // give the screener whatever width inherited its index.
              columnSizes: screenerShown
                ? columnSizes
                : restoreColumnSizes(layout, columnSizes),
            })
          }
          onRowSizes={(rowSizes) =>
            !fullscreenSection &&
            dispatch({
              type: "setRowSizes",
              layoutId: layout.id,
              // The same splice the columns get: with the screener docked top or
              // bottom it is a *row* that collapses when it is hidden, so a drag
              // on the seam between two charts would otherwise save a list one
              // entry short and give the screener back the wrong height.
              rowSizes: screenerShown ? rowSizes : restoreRowSizes(layout, rowSizes),
            })
          }
          onScreenerChange={(sectionId, patch) =>
            dispatch({ type: "updateScreener", sectionId, patch })
          }
          onSectionConfig={(sectionId, config) =>
            dispatch({ type: "setSectionConfig", sectionId, config })
          }
          onMacroSectionConfig={(sectionId, config) =>
            dispatch({ type: "setMacroSectionConfig", sectionId, config })
          }
          chartJump={chartJump}
          authoring={publishTarget != null}
          onPaneHeights={(chartGroupId, heights) =>
            dispatch({ type: "setPaneHeights", chartGroupId, heights })
          }
          // The existing `setPanes`, not an action of its own: a reorder is a
          // permutation of the array the chart already owns, and the view has
          // computed the finished one. It takes the full-save path, which is
          // right — the granular endpoints address a pane by id and would have
          // nothing to say about where it now sits.
          onPaneOrder={(chartGroupId, panes) =>
            dispatch({ type: "setPanes", chartGroupId, panes })
          }
          eventsFor={eventsFor}
          onScreenerSchema={(_sectionId, schema) => setScreenerSchema(schema)}
          // Picking a row in the screener is how you choose what to chart —
          // the reason to have both on screen at once.
          onSelectSymbol={
            group
              ? (symbol) => chartSymbol(group.id, symbol)
              : undefined
          }
          // The × at the end of the screener's own group bar. Hiding lives on
          // the panel; *showing* cannot, because there is nothing left to click
          // — that is the strip's third slot below. View state either way (D8):
          // this sets `screenerShown` and nothing is written back.
          onHideScreener={() => setDock(closePanel(dock, "screener"))}
          // The screener bar's ▾ list: ＋ New is the editor strip's own add, and
          // ✎ Edit opens the editor as the strip key does, on Columns.
          onNewScreen={addNewScreen}
          // ▥ on a screen still opens the editor's Columns tab; the screen
          // list's ✎ Edit this screen opens the builder (owner, 2026-10-26).
          onEditScreen={(via) => {
            if (via === "columns") {
              setEditorTab("columns");
              setEditorOpen(true);
            } else setBuilder("edit");
          }}
          // The data panel, standing in the screener's slot. Passing the node
          // rather than a flag keeps `SectionGrid` unaware that a data panel
          // exists — it renders what it is handed in that cell, which is what
          // lets this stay view state (D8) with no layout change behind it.
          // The dock: one or two panels in the screener's cell. The screener is
          // the grid's own; every other panel is handed in as a node, which
          // keeps `SectionGrid` unaware that a data panel exists.
          dock={
            layoutHasDock && !fullscreenSection
              ? {
                  // A detached panel is in its window, never also here.
                  panels: dock.panels.filter((p) => p !== "data" || !detached),
                  nodes: {
                    data: dataPanelSymbol ? (
                      <DockedDataPanel
                        symbol={dataPanelSymbol!}
                        tab={dataTab}
                        onTab={setDataTab}
                        onClose={closeDataPanel}
                        onDetach={detachable ? detachDataPanel : undefined}
                        detachRefused={detachRefused}
                        // The panel was opened for a chart group, so following a peer
                        // re-points that group — the panel and the chart stay describing
                        // the same company. `symbol` is in `ChartGroupView`'s
                        // `structuralKey`, so this dispatch *is* the refetch.
                        onSymbol={
                          dataTargetId
                            ? (symbol) =>
                                chartSymbol(dataTargetId, symbol)
                            : undefined
                        }
                        // The Summary tab describes *this* chart — its interval, the
                        // indicators on it, and a picture of it.
                        chartGroupId={dataTargetId ?? undefined}
                      />
                    ) : (
                      <DockEmpty>Chart a symbol to see its data here.</DockEmpty>
                    ),
                    community: communityOn ? communityPanel : (
                      <DockEmpty>Community is not available in this app.</DockEmpty>
                    ),
                    // A saved dock can name `ai` on an account or brand that
                    // has lost it; the half says so rather than standing empty.
                    ai: chatOn ? aiPanel : <DockEmpty>QueVee is not available here.</DockEmpty>,
                  },
                  sizes: dockSizes(dock),
                  direction: dockDirection(screenerPlacement(layout)),
                  onSizes: (sizes) =>
                    dispatch({ type: "setDockSizes", layoutId: layout.id, sizes }),
                  onSwap: () => setDock(swapPanels(dock)),
                }
              : undefined
          }
          toolbar={
            // Rendered whenever the strip has anything to hold — not only when
            // a screener exists. Chart type and indicators live here now, and
            // gating the whole strip on the screener would mean a chart-only
            // layout has no way to reach either. `group` rather than `pricePlot`
            // alone, because a price pane whose last plot was removed still has
            // indicators to add — that pane deliberately survives losing them.
            (hasScreener || group) &&
            anchor &&
            (() => {
              /**
               * The saved conditions, pinned beside the strip for this layout
               * (`Dock.pinned`, owner 2026-10-26) or opened on hover. One
               * builder for both, so the pinned panel and the popover cannot
               * drift apart.
               */
              const conditionsPinned =
                !!savedScreener && !!savedScreenerSection && layoutHasDock && isPinned(dock, "conditions");
              const toggleConditionsPin = () => {
                conditionsHover.dismiss();
                setDock(togglePin(dock, "conditions"));
              };
              const conditionsPicker = (pinned: boolean) =>
                savedScreener && savedScreenerSection ? (
                        <SavedFilterPicker
                          filters={savedScreenerShown?.savedFilters ?? []}
                          links={savedScreenLinks}
                          categoryOrder={savedScreener.savedFilterCategoryOrder}
                          selected={selectedFilterIds(savedScreener)}
                          describe={(f) => summarizeFilter(f.filter)}
                          sealedBy={sealedByName(screens, savedScreener.screenRef)}
                          onClose={conditionsHover.dismiss}
                          // 📌 docks it beside the strip for this layout
                          // (`Dock.pinned`); the key's click does the same.
                          pin={{ pinned, onToggle: toggleConditionsPin }}
                          // Under the empty state: a new screen starts with
                          // the standard conditions to add (owner, 2026-10-26).
                          presets={{
                            filters: savedScreener.savedFilters ?? [],
                            allowed: holds,
                            onAdd: (preset) =>
                              dispatch({
                                type: "updateScreener",
                                sectionId: savedScreenerSection.id,
                                patch: {
                                  savedFilters: [
                                    ...(savedScreener.savedFilters ?? []),
                                    presetCopy(preset, mintUlid()),
                                  ],
                                },
                              }),
                          }}
                          // Edit and add both open the builder's Filters tab. A
                          // condition editor inside a 260px popover would be
                          // cramped, and two places to build a filter is the
                          // duplication the picker was meant to remove.
                          onAdd={() => {
                            dispatch({
                              type: "updateScreener",
                              sectionId: savedScreenerSection.id,
                              patch: { filter: { op: "all", children: [] } },
                            });
                            conditionsHover.dismiss();
                            setEditorTab("filters");
                            setEditorOpen(true);
                          }}
                          onEdit={(f) => {
                            dispatch({
                              type: "updateScreener",
                              sectionId: savedScreenerSection.id,
                              patch: {
                                filter: f.filter,
                                activeFilterRef: f.id,
                                activeFilterRefs: [f.id],
                              },
                            });
                            conditionsHover.dismiss();
                            setEditorTab("filters");
                            setEditorOpen(true);
                          }}
                          // ★ and group order: only on a library this caller can
                          // write — no screen, or one of their own (`ScreenerView`
                          // decides the same for the Filters tab).
                          {...(!savedScreener.screenRef ||
                          screens.some(
                            (sc) =>
                              sc.key === savedScreener.screenRef &&
                              (sc.ownerKind === "user" || isAuthored(sc, publishTarget)),
                          )
                            ? {
                                onToggleFavorite: (f: SavedScreenerFilter) =>
                                  dispatch({
                                    type: "updateScreener",
                                    sectionId: savedScreenerSection.id,
                                    patch: {
                                      savedFilters: toggleFavorite(savedScreener.savedFilters ?? [], f.id),
                                    },
                                  }),
                                onMoveCategory: (category: string, delta: -1 | 1) =>
                                  dispatch({
                                    type: "updateScreener",
                                    sectionId: savedScreenerSection.id,
                                    patch: {
                                      savedFilterCategoryOrder: moveCategory(
                                        savedScreener.savedFilters ?? [],
                                        savedScreener.savedFilterCategoryOrder,
                                        category,
                                        delta,
                                      ),
                                    },
                                  }),
                              }
                            : {})}
                          onDelete={(f) =>
                            dispatch({
                              type: "updateScreener",
                              sectionId: savedScreenerSection.id,
                              patch: {
                                savedFilters: (savedScreener.savedFilters ?? []).filter(
                                  (x) => x.id !== f.id,
                                ),
                                // Dropped from the applied set too, or the
                                // screener keeps filtering by something the list
                                // no longer offers.
                                activeFilterRefs: (savedScreener.activeFilterRefs ?? []).filter(
                                  (id) => id !== f.id,
                                ),
                              },
                            })
                          }
                          onChange={(ids) =>
                            dispatch({
                              type: "updateScreener",
                              sectionId: savedScreenerSection.id,
                              patch: applySelection(savedScreener, ids),
                            })
                          }
                        />
                ) : null;

              // Three groups of keys, built once and arranged two ways below:
              // the chart keys, the dock keys, and the drawing tools.
              const chartKeys = (
                <>
                  {/* Chart type leads the chart keys: it belongs to the charts,
                      whose side of the seam the strip is attached to. Beside a
                      screener on the left or right the screener's own buttons
                      follow below; beside one docked on top or below, these
                      move down beside the charts (owner, 2026-10-07). */}
                  {group && priceSeries && (
                    <ChartTypeButton
                      value={chartTypeChoice(group)}
                      onChange={(choice: ChartTypeChoice) =>
                        dispatch({ type: "setChartTypeChoice", groupId: group.id, choice })
                      }
                    />
                  )}
                  {/* Indicators sit directly under chart type, on the chart's
                      side of the strip. `group` is the *active* chart, so an
                      indicator lands on the pane you last clicked rather than
                      on the first one in the layout. */}
                  {group && (
                    <IndicatorButton
                      group={group}
                      mintId={mintUlid}
                      editRequest={editRequest}
                      // The panes arrive computed — `lib/add-indicator` minted
                      // the pane, the axis and a plot per output, exactly as
                      // the layout panel hands over a finished layout.
                      onPanes={(panes) =>
                        dispatch({ type: "setPanes", chartGroupId: group.id, panes })
                      }
                      // Three existing actions rather than a fourth combined
                      // one: `setChartType` is the same action the chart-type
                      // button uses (it addresses a plot, so it already fits),
                      // and style and palette are separate contract nodes. A
                      // patch usually carries exactly one of the three.
                      // Its own action rather than folded into `onPlot`: this
                      // writes the pane's *axis*, which is a different contract
                      // node from a plot and is shared by everything drawn on
                      // it — the whole reason the control is named for the pane.
                      onScale={(paneId, scale) =>
                        dispatch({ type: "setPaneScale", chartGroupId: group.id, paneId, scale })
                      }
                      onPlot={(plotId, patch) => {
                        if (patch.type) {
                          dispatch({ type: "setChartType", plotId, chartType: patch.type });
                        }
                        if (patch.style) {
                          dispatch({ type: "setPlotStyle", plotId, style: patch.style });
                        }
                        if (patch.hidden !== undefined) {
                          dispatch({ type: "setPlotHidden", plotId, hidden: patch.hidden });
                        }
                        if (patch.palette) {
                          dispatch({ type: "setPlotPalette", plotId, palette: patch.palette });
                        }
                      }}
                    />
                  )}
                  {/* The journal, on the chart's side of the strip because a
                      plan is about the symbol on screen. It both lists what is
                      already planned and starts a new one — which is why the
                      toolbar's "New plan" has since gone: that one armed a plan
                      from a row showing no plans, so nothing came back to say it
                      had worked. Starting one is now beside what it produces,
                      or drawn on the chart.

                      **Absent for a composite.** `AAPL/SPY` is not an
                      instrument anyone can buy, so a plan against it would
                      reach the journal, the freeze and the paper simulator
                      carrying a symbol no fill can ever be matched to. Hidden
                      rather than disabled: there is nothing here to enable
                      later, and a greyed button invites a hunt for the reason.
                      A pair trade is a real thing and would want two legs with
                      their own sizes, which is a schema change rather than a
                      re-enabled button — see docs/TODO.md. */}
                  {group && available("plan") && journalEnabled() && !isComposite(group.symbol) && (
                    <div style={{ position: "relative" }}>
                      <GridToolbarButton
                        label={`Plans for ${group.symbol}`}
                        active={plansOpen}
                        onClick={() => setPlansOpen((open) => !open)}
                      >
                        <PlanIcon />
                      </GridToolbarButton>
                      {plansOpen && (
                        <div style={filterPopoverAnchor(anchor.side)}>
                          <PlanPanel
                            symbol={group.symbol}
                            reloadKey={journalStamp}
                            // A delete here has to reach the chart, which draws
                            // its levels from this component's copy.
                            onChanged={() => setJournalStamp((n) => n + 1)}
                            onNew={() => {
                              setEditingPlan(null);
                              setPlanningSymbol(group.symbol);
                            }}
                            onEdit={(plan) => {
                              setEditingPlan(plan);
                              setPlanningSymbol(plan.symbol);
                            }}
                            onClose={() => setPlansOpen(false)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
              const dockKeys = (
                <>
                  {/* Everything known about the active chart's symbol. Directly
                      above the screener keys because it is the thing that takes
                      the screener's place: the pair that swaps sits together,
                      and the reader learns the swap from where the keys are.

                      **One key for the layout, not one per chart.** There has
                      only ever been one panel — pressing another chart's `⊞`
                      moved it rather than opening a second — so a key per
                      toolbar was three controls for one thing. It aims at the
                      active chart, which is what the ring already means and
                      what the indicator picker beside it already does.

                      Absent with no chart: the panel describes a symbol, and
                      with nothing charted there is no symbol to describe. */}
                  {/* Split — two panels in the dock instead of one. Above the
                      data key because it decides where that key's panel goes.
                      Lit is split on; there is no stored flag, two panels
                      showing *is* split (lib/dock.ts). */}
                  {layoutHasDock && (
                    <GridToolbarButton
                      label={
                        isSplit(dock)
                          ? "Unsplit — close the second panel"
                          : "Split — show two panels in the dock"
                      }
                      active={isSplit(dock)}
                      onClick={() => {
                        const next = toggleSplit(dock);
                        if (isShowing(next, "data") && group) setDataPanelId(group.id);
                        if (!isShowing(next, "screener")) setEditorOpen(false);
                        setDock(next);
                      }}
                    >
                      <SplitIcon />
                    </GridToolbarButton>
                  )}
                  {group && (
                    <div
                      ref={dataKeyRef}
                      // `data-tour`: the key's own label carries the symbol, so
                      // it is not a stable selector. The tour targets the button
                      // inside this wrapper. See lib/tour.ts.
                      data-tour="symbol-data"
                      style={{ position: "relative" }}
                      // On the wrapper, not the key: `GridToolbarButton` takes a
                      // click and nothing else, and hover intent needs both ends
                      // of the gesture. Measured now rather than tracked — a
                      // rect held across a divider drag anchors the peek to
                      // where the key used to be.
                      onPointerEnter={(e) => {
                        setDataPanelId(group.id);
                        setDataAnchor(dataKeyRef.current?.getBoundingClientRect() ?? new DOMRect());
                        dataHover.enterTrigger(e.pointerType);
                      }}
                      onPointerLeave={dataHover.leaveTrigger}
                    >
                      <GridToolbarButton
                        label={
                          dataKept
                            ? `Close the data panel`
                            : `Data for ${group.symbol} — hover to peek, click to open it here`
                        }
                        active={dataKept}
                        onClick={() => {
                          setDataAnchor(
                            dataKeyRef.current?.getBoundingClientRect() ?? new DOMRect(),
                          );
                          if (layoutHasDock) {
                            // A panel key: open it (replacing the one panel, or
                            // the second when split) or close it. A peek that is
                            // up goes first, so the panel is not in two places.
                            if (dataHover.pinned) dataHover.togglePin();
                            const next = togglePanel(dock, "data");
                            if (isShowing(next, "data")) setDataPanelId(group.id);
                            // The editor edits the screener; with the screener
                            // gone from the dock it would be a column of pickers
                            // beside a panel nobody can see.
                            if (!isShowing(next, "screener")) setEditorOpen(false);
                            setDock(next);
                            return;
                          }
                          setDataPanelId(group.id);
                          // No dock to hold it, so it floats as the dialog: a
                          // toggle, as a lit key means.
                          dataHover.togglePin();
                        }}
                      >
                        <DataPanelIcon />
                      </GridToolbarButton>
                    </div>
                  )}
                  {/* One slot, three jobs now, and they are still never two at
                      once: with no screener in the layout it adds one, with the
                      data panel holding the slot it takes the slot back, and
                      otherwise it edits the screener it is looking at. */}
                  {!hasScreener && (
                    <GridToolbarButton
                      label="Add screener"
                      onClick={() => addScreenerAt("right")}
                    >
                      <ShowScreenerIcon />
                    </GridToolbarButton>
                  )}
                  {hasScreener &&
                    (screenerInDock ? (
                      <div
                        style={{ position: "relative" }}
                        // **Hover lists the screens; the click still opens the
                        // editor.** The data-panel key beside it established
                        // this pairing — peek on hover, act on click — and it
                        // suits this key for the same reason: switching screen
                        // is the thing you do most often and it needed the
                        // editor open and a strip read before you could.
                        onPointerEnter={(e) => screensHover.enterTrigger(e.pointerType)}
                        onPointerLeave={screensHover.leaveTrigger}
                      >
                      <GridToolbarButton
                        label={editorOpen ? "Close screener editor" : "Edit screener"}
                        active={editorOpen}
                        onClick={() => {
                          // Columns every time this button opens it. The tab is
                          // remembered only so the filter picker can send you
                          // straight to Filters; without the reset, using that once
                          // would leave the editor opening there forever.
                          if (!editorOpen) setEditorTab("columns");
                          setEditorOpen((open) => !open);
                        }}
                      >
                        <EditScreenerIcon />
                      </GridToolbarButton>
                      {screensHover.shown && screens.length > 0 && savedScreenerSection && (
                        // Onto the charts, which is `filterPopoverAnchor`'s rule
                        // and its reason: the strip stands between the charts
                        // and the screener, so opening the other way would cover
                        // the grid this is about to change.
                        <div style={filterPopoverAnchor(anchor.side)}>
                          <ScreenList
                            screens={screens}
                            selected={savedScreener?.screenRef ?? defaultScreen(screens)?.key}
                            onPointerEnter={screensHover.enterSurface}
                            onPointerLeave={screensHover.leaveSurface}
                            // A TopDown over the screen showing, when that one
                            // is an ordinary screen (`docs/features/topdown.md`).
                            onNewTopDown={
                              savedScreener?.screenRef && !savedScreener.topDown
                                ? () => {
                                    screensHover.dismiss();
                                    const baseKey = savedScreener.screenRef!;
                                    const baseName =
                                      screens.find((s) => s.key === baseKey)?.name ?? "screen";
                                    void addScreen({
                                      name: nextFreeName(screens, `TopDown · ${baseName}`),
                                      groupCodes: savedScreener.group ? [savedScreener.group] : [],
                                      topDown: newTopDown(baseKey),
                                    })
                                      .then((made) =>
                                        applyScreenTo({
                                          key: made.key,
                                          config: savedScreener,
                                          load: fetchPreset,
                                          onConfigChange: (patch) =>
                                            dispatch({
                                              type: "updateScreener",
                                              sectionId: savedScreenerSection!.id,
                                              patch,
                                            }),
                                        }),
                                      )
                                      .catch(() => {});
                                  }
                                : undefined
                            }
                            showSeasonality={seasonalityTier !== null}
                            // A Seasonality screen over the screen showing, when
                            // that one is ordinary (`docs/features/seasonality.md`).
                            onNewSeasonality={
                              seasonalityTier !== null &&
                              savedScreener?.screenRef &&
                              // Only over a screen in the catalog: a stale
                              // reference made a Seasonality screen over a base
                              // nobody could open (2026-10-23).
                              screens.some((s) => s.key === savedScreener.screenRef) &&
                              !savedScreener.topDown &&
                              !savedScreener.seasonality
                                ? () => {
                                    screensHover.dismiss();
                                    const baseKey = savedScreener.screenRef!;
                                    const baseName =
                                      screens.find((s) => s.key === baseKey)?.name ?? "screen";
                                    void addScreen({
                                      name: nextFreeName(screens, `Seasonality · ${baseName}`),
                                      groupCodes: savedScreener.group ? [savedScreener.group] : [],
                                      seasonality: newSeasonality(baseKey),
                                    })
                                      .then((made) =>
                                        applyScreenTo({
                                          key: made.key,
                                          config: savedScreener,
                                          load: fetchPreset,
                                          onConfigChange: (patch) =>
                                            dispatch({
                                              type: "updateScreener",
                                              sectionId: savedScreenerSection!.id,
                                              patch,
                                            }),
                                        }),
                                      )
                                      .catch(() => {});
                                  }
                                : undefined
                            }
                            onSelect={(key) => {
                              screensHover.dismiss();
                              void applyScreenTo({
                                key,
                                config: savedScreener!,
                                // Draft-aware for a screen being authored — see `screenLoader`.
                                load: screenLoader(publishTarget),
                                onConfigChange: (patch) =>
                                  dispatch({
                                    type: "updateScreener",
                                    sectionId: savedScreenerSection.id,
                                    patch,
                                  }),
                              });
                            }}
                          />
                        </div>
                      )}
                      </div>
                    ) : (
                      // Not in the dock — hidden, or another panel standing in
                      // its place — so this reveals rather than edits. Editing a
                      // screener you cannot see is the one job it must not offer.
                      <GridToolbarButton
                        label="Show screener"
                        onClick={() => setDock(openPanel(dock, "screener"))}
                      >
                        <ShowScreenerIcon />
                      </GridToolbarButton>
                    ))}
                  {savedScreenerSection && (
                  <div
                    style={{ position: "relative" }}
                    // Hover opens it, click keeps it open — the same pairing the
                    // two keys above use. It opened on click alone, which is one
                    // gesture more than looking at it needs.
                    onPointerEnter={(e) => conditionsHover.enterTrigger(e.pointerType)}
                    onPointerLeave={conditionsHover.leaveTrigger}
                  >
                    <GridToolbarButton
                      label="Saved conditions"
                      active={
                        conditionsPinned ||
                        (savedScreener?.activeFilterRefs?.length ?? 0) > 0
                      }
                      // Click pins it beside the strip; hover still peeks.
                      onClick={toggleConditionsPin}
                    >
                      <ConditionIcon />
                    </GridToolbarButton>
                    {conditionsHover.shown && !conditionsPinned && savedScreener && savedScreenerSection && (
                      // Opens onto the charts: the toolbar sits between them and
                      // the screener, so opening the other way would cover the
                      // grid it is filtering.
                      <div
                        // `data-tour`: the surface the tour opens to show what
                        // a saved condition is. See lib/tour.ts.
                        data-tour="conditions"
                        style={filterPopoverAnchor(anchor.side)}
                      >
                        {conditionsPicker(false)}
                      </div>
                    )}
                  </div>
                  )}
                  {/* Community — below Saved conditions (owner, 2026-09-29). A
                      panel key like the data key above it: it opens Community
                      in the dock (replacing the one panel, or the second when
                      split) and closes it again. */}
                  {communityOn && (
                    <GridToolbarButton
                      label={
                        communityDocked || communityFloating
                          ? "Close Community"
                          : "Community — your groups' spaces and Open Houses"
                      }
                      active={communityDocked || communityFloating}
                      onClick={() => {
                        if (!layoutHasDock) {
                          setCommunityFloating((open) => !open);
                          return;
                        }
                        const next = togglePanel(dock, "community");
                        if (!isShowing(next, "screener")) setEditorOpen(false);
                        setDock(next);
                      }}
                    >
                      <CommunityIcon />
                    </GridToolbarButton>
                  )}
                  {/* AI — below Community, the same kind of panel key: it opens
                      the chat in the dock and closes it again. Only where the
                      brand put `agent` on the strip (`chartToolbar`). */}
                  {chat.key && (
                    <GridToolbarButton
                      label={aiDocked || aiFloating ? "Close AI" : "AI (QueVee) — ask about this chart or screen"}
                      active={aiDocked || aiFloating}
                      onClick={() => {
                        if (!layoutHasDock) {
                          setAiFloating((open) => !open);
                          return;
                        }
                        const next = togglePanel(dock, "ai");
                        if (!isShowing(next, "screener")) setEditorOpen(false);
                        setDock(next);
                      }}
                    >
                      <QueVeeMark size={22} playOnClick />
                    </GridToolbarButton>
                  )}
                </>
              );
              const drawKeys = (
                <>
                  {group && available("annotations") && (
                    <AnnotationTools
                      active={armedTool}
                      onArm={setArmedTool}
                      activeTrading={armedTrading}
                      onArmTrading={setArmedTrading}
                      side={anchor.side}
                      onClear={() =>
                        // `clearAnnotations` drops the key rather than storing
                        // an empty array: qv-data collapses an empty container
                        // to absent, so a saved `[]` comes back missing and a
                        // save would differ from its own reload.
                        {
                          // **Both halves, because there are two.** The contract
                          // ones go through the reducer; the financial-tools
                          // ones live only on the surface and are unreachable
                          // from the workspace, which is why clearing appeared
                          // to do nothing to them.
                          dispatch({ type: "clearAnnotations", chartGroupId: group.id });
                          setClearNonce((n) => n + 1);
                        }
                      }
                    />
                  )}
                </>
              );

              /* **The chart keys are two halves, and the split is the point.**

                 The top half acts on *the chart*: what it draws, which
                 indicators are on it. Those change the workspace and are always
                 available.

                 The bottom half acts on *the pointer*: it arms a tool, and the
                 next drag on the canvas creates something. Exactly one can be
                 armed at a time.

                 Run together as one column of fourteen icons, the two read as
                 undifferentiated and a user hunting for a trendline scans past
                 the screener's filter button to find it. Split in two, "what is
                 on this chart" and "what am I about to draw" are answered in
                 different places on screen, which is how every drawing app
                 people already know is arranged.

                 The seam between them is not decoration either: it is the only
                 thing that says the column below is a different kind of control
                 from the column above, and without it the split reads as an
                 accidental gap. */
              const halves = (upper: ReactNode) => (
                <>
                  <div style={STRIP_HALF}>{upper}</div>
                  <div aria-hidden style={STRIP_SEAM} />
                  <div style={STRIP_HALF}>{drawKeys}</div>
                </>
              );

              const strip = {
                column: anchor.column,
                side: anchor.side,
                rows: anchor.rows,
                // 38 for 34px keys plus the strip's 1px rule and a hair either
                // side. The keys are the measurement; the strip follows them, not
                // the other way round.
                width: 38,
                ...(conditionsPinned
                  ? { pinned: { width: CONDITIONS_PINNED_WIDTH, children: conditionsPicker(true) } }
                  : {}),
              };
              // Screener docked top or bottom: one strip down the whole left
              // edge, the dock keys beside the screener's rows and every chart
              // key beside the charts' (owner, 2026-10-07, "option C"). Chart
              // type and indicators came down from the top group to join the
              // drawing tools — beside the screener they were a panel away from
              // the chart they change. Listed in screen order, top part first.
              if (anchor.split) {
                const { dock: dockRows, chart: chartRows } = anchor.split;
                const dockPart = { rows: dockRows, children: <div style={STRIP_HALF}>{dockKeys}</div> };
                const chartPart = { rows: chartRows, children: halves(chartKeys) };
                return {
                  ...strip,
                  parts: dockRows[0] < chartRows[0] ? [dockPart, chartPart] : [chartPart, dockPart],
                };
              }
              // Left, right, or no screener showing: one column, chart keys
              // first and the screener's own below them, as it always was.
              return {
                ...strip,
                children: halves(
                  <>
                    {chartKeys}
                    {dockKeys}
                  </>,
                ),
              };
            })()
          }
          insertedColumn={
            editorOpen && screenerInDock && screener && screenerSection && anchor
              ? {
                  // Docked on the strip's far side, so the editor lands between
                  // the charts and the screener it edits — which on the left and
                  // on the two horizontal placements is *before* the chart
                  // region's edge column rather than after it.
                  at: anchor.side === "right" ? anchor.column + 1 : anchor.column,
                  size: editorSize,
                  onSize: setEditorSize,
                  children: (
                    // **The screen tabs sit above the editor's own tabs**, not
                    // above the grid. Choosing *which screen* is the outermost
                    // question the editor answers — Columns, Filters, Format and
                    // Presets are all "of what?" — and putting the strip over the
                    // grid instead made the panel carry a control for a thing the
                    // panel does not edit.
                    <div style={EDITOR_COLUMN}>
                      {screens.length > 0 && (
                        <ScreenTabs
                          screens={screens}
                          publishTo={publishTarget}
                          selected={screener.screenRef ?? defaultScreen(screens)?.key}
                          onSelect={(key) =>
                            void applyScreenTo({
                              key,
                              // The stored panel — links as links — for the
                              // comparison and the pruned ticks.
                              config: storedScreener ?? screener,
                              // Draft-aware for a screen being authored: the
                              // published screen here, over an open draft,
                              // would be written back as the draft.
                              load: screenLoader(publishTarget),
                              onConfigChange: (patch) =>
                                dispatch({
                                  type: "updateScreener",
                                  sectionId: screenerSection.id,
                                  patch,
                                }),
                            })
                          }
                          onAdd={addNewScreen}
                          onRename={(key, name) => void renameScreen(key, name).catch(() => {})}
                          // **From the panel, not from the club's row.** What
                          // the panel holds is the merge — their columns with
                          // your widths and your additions — and that is what
                          // "duplicate this" means to somebody looking at it.
                          // Copying the base alone would silently drop the half
                          // they had added.
                          onDuplicate={() => {
                            void addScreen({
                              name: nextFreeName(screens, "Copy"),
                              groupCodes: screener.group ? [screener.group] : [],
                              // The **stored** panel: a linked column goes as its
                              // link and view, so the copy stays in step with
                              // the group as the original does.
                              columns: stripLinks(storedScreener?.columns ?? []),
                              filter: screener.filter,
                              sort: screener.sort,
                              // The open ones and the linked ones — a sealed
                              // condition is the group's rule, and goes nowhere
                              // but its own screen. `filter` holds none of it.
                              savedFilters: libraryForAnotherScreen(storedScreener?.savedFilters ?? []),
                            })
                              .then((made) =>
                                dispatch({
                                  type: "updateScreener",
                                  sectionId: screenerSection.id,
                                  patch: { screenRef: made.key },
                                }),
                              )
                              .catch(() => {});
                          }}
                          onRemove={(key) => {
                            // Move off it *before* it goes, or the panel points
                            // at a tab that is no longer in the strip.
                            const next = screens.find((s) => s.key !== key);
                            if (next) {
                              void applyScreenTo({
                                key: next.key,
                                config: storedScreener ?? screener,
                                load: screenLoader(publishTarget),
                                onConfigChange: (patch) =>
                                  dispatch({
                                    type: "updateScreener",
                                    sectionId: screenerSection.id,
                                    patch,
                                  }),
                              });
                            }
                            void removeScreen(key).catch(() => {});
                          }}
                        />
                      )}
                      <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex" }}>
                    {screener.topDown ? (
                      // A TopDown screen's editor: Sector · Industry · Symbols.
                      <TopDownEditor
                        topDown={screener.topDown}
                        screen={screens.find((s) => s.key === screener.screenRef)}
                        screens={screens}
                        compileExpr={compileExpr}
                        onChange={(topDown) =>
                          dispatch({
                            type: "updateScreener",
                            sectionId: screenerSection.id,
                            patch: { topDown },
                          })
                        }
                        onOpenBase={(key) =>
                          void applyScreenTo({
                            key,
                            config: storedScreener ?? screener,
                            load: screenLoader(publishTarget),
                            onConfigChange: (patch) =>
                              dispatch({
                                type: "updateScreener",
                                sectionId: screenerSection.id,
                                patch,
                              }),
                          })
                        }
                        onClose={() => setEditorOpen(false)}
                      />
                    ) : screener.seasonality ? (
                      // A Seasonality screen is set from its own filter row;
                      // its columns and conditions are the base screen's.
                      <div style={{ padding: 12, fontSize: 12.5, color: "var(--qf-text-muted)", display: "flex", flexDirection: "column", gap: 8, flex: "1 1 auto" }}>
                        <span style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                          <span style={{ flex: "1 1 auto" }}>
                            A Seasonality screen is set from the row under its bar: mode, basis, years, Hit and Avg, and the start date.
                          </span>
                          <button
                            type="button"
                            aria-label="Close screener editor"
                            title="Close"
                            onClick={() => setEditorOpen(false)}
                            style={{ border: "none", background: "transparent", color: "var(--qf-text-muted)", cursor: "pointer", fontSize: 14, padding: "0 4px" }}
                          >
                            ×
                          </button>
                        </span>
                        <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            style={{ border: "1px solid var(--qf-border)", borderRadius: 6, padding: "3px 9px", background: "transparent", color: "var(--qf-text)", cursor: "pointer" }}
                            onClick={() =>
                              void applyScreenTo({
                                key: screener.seasonality!.base,
                                config: storedScreener ?? screener,
                                load: screenLoader(publishTarget),
                                onConfigChange: (patch) =>
                                  dispatch({ type: "updateScreener", sectionId: screenerSection.id, patch }),
                              })
                            }
                          >
                            Edit the base screen ↗
                          </button>
                          {/* Authoring only: the group's members get the screen
                              as a tab (docs/features/group-accounts.md). */}
                          {publishTarget && (
                            <button
                              type="button"
                              disabled={seasPublish === "busy"}
                              style={{ border: "1px solid var(--qf-border)", borderRadius: 6, padding: "3px 9px", background: "transparent", color: "var(--qf-text)", cursor: "pointer" }}
                              onClick={() => {
                                setSeasPublish("busy");
                                publishToClub({
                                  name: screens.find((s) => s.key === screener.screenRef)?.name ?? "Seasonality",
                                  description: "",
                                  columns: screener.columns,
                                  filter: screener.filter,
                                  sort: screener.sort,
                                  seasonality: screener.seasonality,
                                })
                                  .then(() => setSeasPublish("idle"))
                                  .catch(() => setSeasPublish("failed"));
                              }}
                            >
                              ⇪ Publish to {publishTargetName ?? publishTarget}
                            </button>
                          )}
                          {seasPublish === "failed" && <span>Couldn't publish — try again.</span>}
                        </span>
                        {publishTarget && (
                          <PublishedScreens
                            screens={screens}
                            group={publishTarget}
                            groupName={publishTargetName ?? publishTarget}
                            onChanged={loadScreens}
                          />
                        )}
                      </div>
                    ) : (
                    <ScreenerBuilder
                      variant="embedded"
                      presetGate={holds}
                      initialTab={editorTab}
                      config={builderScreener ?? screener}
                      fields={screenerSchema.fields}
                      meta={screenerSchema.meta}
                      timings={screenerSchema.timings}
                      simulating={simulating}
                      onSimulate={
                        isAdmin
                          ? () => {
                              setSimulating(true);
                              void runSimulation({ chartUsers: 10 })
                                .then((result) => {
                                  // Logged rather than rendered, for now: the
                                  // result is a nested record an admin reads
                                  // once, and a panel for it is a bigger
                                  // surface than the question deserves until
                                  // somebody has run it a few times.
                                  // eslint-disable-next-line no-console
                                  console.log("[sim]", result);
                                  const impact = result.chart.impactFactor;
                                  window.alert(
                                    `Screener ${Math.round(result.screener.ms)}ms
` +
                                      `Charts alone (median) ${result.chart.baseline.median}ms
` +
                                      `Charts under load ${result.chart.loaded.median}ms
` +
                                      `Impact ${impact == null ? "n/a" : `${impact}x`}

` +
                                      `Full detail in the console.`,
                                  );
                                })
                                .catch((err: unknown) =>
                                  window.alert(
                                    `Simulation failed: ${
                                      err instanceof Error ? err.message : String(err)
                                    }`,
                                  ),
                                )
                                .finally(() => setSimulating(false));
                            }
                          : undefined
                      }
                      onConfigChange={(patch) =>
                        dispatch({
                          type: "updateScreener",
                          sectionId: screenerSection.id,
                          patch,
                        })
                      }
                      onSavedFiltersChange={(savedFilters) =>
                        dispatch({
                          type: "updateScreener",
                          sectionId: screenerSection.id,
                          patch: { savedFilters },
                        })
                      }
                      mintId={mintUlid}
                      resolveToken={resolveToken}
                      compileExpr={compileExpr}
                      dslCatalog={dslCatalog}
                      columnsSplit={columnsSplit}
                      onColumnsSplit={(split) => {
                        setColumnsSplit(split);
                        saveColumnsSplit(split);
                      }}
                      // Undefined without the plan, so the control is absent
                      // rather than present and refused — the same rule
                      // `presets` states two lines below.
                      describeExpr={describeEnabled() ? describeExpr : undefined}
                      // Presets: what this account may apply, and — for an
                      // admin who belongs to a club — publishing this screen as
                      // one. `publishPreset` is `undefined` for everyone else,
                      // so the control is absent rather than present and
                      // refused, which is the rule the app menu already follows.
                      publishPreset={publishTarget ? publishToClub : undefined}
                      publishedSlot={
                        publishTarget ? (
                          <PublishedScreens
                            screens={screens}
                            group={publishTarget}
                            groupName={publishTargetName ?? publishTarget}
                            onChanged={loadScreens}
                          />
                        ) : undefined
                      }
                      // Copy from screen… — only while authoring, the way the
                      // Combo screen is assembled from JAS's and JP's
                      // (`docs/plans/group-sharing.md`). Draft-aware loader, so
                      // a group's own screen is copied as the author last left it.
                      copyFromScreen={publishTarget ? copySources : undefined}
                      sealedBy={sealedByName(screens, screener.screenRef)}
                      // Link from screen…, for every member (decision 4) —
                      // but never into a group's screen being authored: a
                      // link there would point every member at a group they
                      // may not hold, and the server refuses it (decision 5).
                      // Copy from screen… is offered there instead.
                      screenLinks={linksOffered(screenLinks, screens, screener.screenRef, publishTarget)}
                      onClose={() => setEditorOpen(false)}
                    />
                    )}
                      </div>
                    </div>
                  ),
                }
              : undefined
          }
        />
        )
      ) : (
        <div
          style={{ flex: 1, background: "var(--qf-background)" }}
          onContextMenu={openMenu}
        />
      )}

      {/* Rendered once by `App`, not per chart, for `SymbolDialog`'s reason: the
          panel is not per-chart, only *where its symbol comes from* is. One
          instance also makes re-pointing it a state change rather than an
          unmount and a remount. */}
      {/* Four forms of one panel, and only one is on screen at a time: a peek
          beside the key, the screener's slot, a dialog for the layout that has
          no slot to take, and a window. The peek has no close button and no drag
          — it is held open by the pointer, and a control you must travel to in
          order to dismiss is one a peek does not need.

          The slot form is not rendered here: it goes to `SectionGrid` as
          `screenerSlot`, because it is *in* the grid rather than over it. */}
      {dataPanelSymbol && dataHover.shown && !dataHover.pinned && !dataInSlot && dataAnchor && (
        <HoveredDataPanel
          symbol={dataPanelSymbol}
          tab={dataTab}
          onTab={setDataTab}
          anchor={dataAnchor}
          onEnter={dataHover.enterSurface}
          onLeave={dataHover.leaveSurface}
          // Onto the charts, away from the strip — the saved-filter popover's
          // rule, and the same reason: the strip's other neighbour is the
          // screener, which is the surface this panel is about to replace.
          // `anchor.side` is where the *strip* hangs, so the panel takes the
          // opposite one.
          side={anchor ? (anchor.side === "right" ? "left" : "right") : undefined}
        />
      )}
      {/* `!dataInSlot`, so the panel is never in two places at once: with a
          screener showing it is already in that slot, rendered by the grid. */}
      {/* One window for every layout: a switch re-points it (lib/data-panel-
          target.ts) and it stays open even on a layout with no chart, because
          closing and reopening it would need a click the switch is not — a
          browser blocks that open — and would land it wherever it chose. */}
      {detached && dataHover.pinned && !dataInSlot && (
        <DetachedWindow
          title={dataPanelSymbol ? symbolLabel(dataPanelSymbol, dataPanelName) : "Data panel"}
          // The window closing *is* the panel coming home, so it returns to
          // docked rather than closing outright — losing what you were reading
          // because you tidied a window away is not what the ✕ meant. A popup
          // the browser refused arrives here too, for the same reason.
          onClose={dockDataPanel}
          // Refusing is not the same as closing: it comes home *and* retires
          // the offer, so the second click cannot repeat the first's silence.
          onRefused={() => setDetachRefused(true)}
        >
          {dataPanelSymbol ? (
            <SymbolDataTabs
              symbol={dataPanelSymbol}
              tab={dataTab}
              onTab={setDataTab}
              chartGroupId={dataTargetId ?? undefined}
              onSymbol={dataTargetId ? (symbol) => chartSymbol(dataTargetId, symbol) : undefined}
            />
          ) : (
            <p style={{ padding: 24, color: "var(--qf-text-muted)" }}>
              No chart in this layout. Switch to a layout with a chart and this window shows it.
            </p>
          )}
        </DetachedWindow>
      )}
      {/* One window per detached view, each following the panel's chart, and
          each — like the panel's window — open across layout switches. Closing
          one brings that view home to the panel's strip. */}
      {detachedViews.map((view) => (
        <DetachedWindow
          key={view}
          name={viewWindowName(view)}
          title={`${dataPanelSymbol ? `${dataPanelSymbol} · ` : ""}${symbolView(view)?.label ?? view}`}
          onClose={() => setDetachedViews((was) => was.filter((v) => v !== view))}
          onRefused={() => setDetachRefused(true)}
        >
          {dataPanelSymbol ? (
            <SymbolDataView
              symbol={dataPanelSymbol}
              view={view}
              chartGroupId={dataTargetId ?? undefined}
              onSymbol={dataTargetId ? (symbol) => chartSymbol(dataTargetId, symbol) : undefined}
            />
          ) : (
            <p style={{ padding: 24, color: "var(--qf-text-muted)" }}>
              No chart in this layout. Switch to a layout with a chart and this window shows it.
            </p>
          )}
        </DetachedWindow>
      ))}
      {dataPanelSymbol &&
        dataHover.pinned &&
        !dataInSlot &&
        !detached && (
          <SymbolDataPanel
            symbol={dataPanelSymbol}
            tab={dataTab}
            onTab={setDataTab}
            onClose={closeDataPanel}
            onDetach={detachable ? detachDataPanel : undefined}
            detachRefused={detachRefused}
            // The panel was opened *for* a chart group, so following a peer
            // re-points that group. `symbol` is in `ChartGroupView`'s
            // `structuralKey`, so this dispatch *is* the refetch.
            onSymbol={
              dataPanelId
                ? (symbol) =>
                    chartSymbol(dataPanelId, symbol)
                : undefined
            }
            // The Summary tab describes *this* chart — its interval, the
            // indicators on it, and a picture of it.
            chartGroupId={dataPanelId ?? undefined}
          />
        )}

      {/* Rendered once by `App`, beside the symbol dialog and for its reason: it
          is not per-chart, and only *which chart it acts on* is — which the
          action context already answers. */}
      {commandsOpen && (
        <CommandSearch ctx={actionContext} onClose={() => setCommandsOpen(false)} />
      )}

      {menuAt && (
        <ContextMenu
          x={menuAt.x}
          y={menuAt.y}
          onClose={() => setMenuAt(null)}
          items={[
            // Plan actions first, and only when the right-click landed on a
            // level: they are about the thing under the pointer, where
            // everything below is about the chart as a whole.
            ...(menuLevel
              ? [
                  {
                    id: "add-target",
                    label: "Add target",
                    onSelect: () => {
                      editTargets(menuLevel.planId, (plan) => [
                        ...(plan.targets ?? []),
                        // Minted here, not server-side: a target is contract
                        // data with a client-mintable id (D4), and the share is
                        // one because `normalise_shares` rescales the set.
                        { id: mintUlid(), price: nextTargetPrice(plan), share: 1 },
                      ]);
                      setMenuAt(null);
                    },
                  },
                  // Only the **last** target, and never the only one — see
                  // `canRemoveTarget`. Removing T1 while T2 stands would leave
                  // "T1" meaning what T2 meant, and removing the only exit
                  // leaves a position riding to the stop with nothing to take
                  // it off. The stop and the entry are not removable at all.
                  ...(menuLevel.kind === "target" &&
                  menuLevel.targetId &&
                  canRemoveTarget(
                    journal.plans.find((p) => p.id === menuLevel.planId) ??
                      ({ targets: [] } as unknown as Plan),
                    menuLevel.targetId,
                  )
                    ? [
                        {
                          id: "remove-target",
                          label: "Remove target",
                          onSelect: () => {
                            editTargets(menuLevel.planId, (plan) =>
                              (plan.targets ?? []).filter((t) => t.id !== menuLevel.targetId),
                            );
                            setMenuAt(null);
                          },
                        },
                      ]
                    : []),
                ]
              : []),
            // Share the chart under the pointer to a group's community: the
            // composer opens with a picture of it and a copy readers can open.
            ...(group && communityOn
              ? [
                  {
                    id: "share-to-group",
                    label: "Share to group…",
                    onSelect: () => {
                      setShareNonce((n) => n + 1);
                      if (layoutHasDock) {
                        if (!communityDocked) setDock(addPanel(dock, "community"));
                      } else {
                        setCommunityFloating(true);
                      }
                      setMenuAt(null);
                    },
                  },
                ]
              : []),
            {
              id: "settings",
              label: "Settings",
              items: [
                {
                  id: "theme",
                  label: "Theme…",
                  onSelect: () => setThemeEditorOpen(true),
                },
                // **Chart templates, reached by pointing at the chart.** Which
                // is also what settles the target: a strip key would drive the
                // *active* chart — the focus ring the indicator picker follows
                // — and both dialogs would need a sentence saying which one
                // that is. Right-clicking names it, so "this chart" is
                // literally true.
                //
                // Absent without a server to ask, rather than present and
                // failing on click, which is the rule every other
                // server-backed control here follows.
                ...(group && chartTemplatesConfigured
                  ? [
                      {
                        id: "save-chart-template",
                        label: "Save chart as template…",
                        onSelect: () => {
                          setTemplateFor({ mode: "save", groupId: group.id });
                          setMenuAt(null);
                        },
                      },
                      {
                        id: "apply-chart-template",
                        label: "Apply chart template…",
                        onSelect: () => {
                          setTemplateFor({ mode: "apply", groupId: group.id });
                          setMenuAt(null);
                        },
                      },
                    ]
                  : []),
                // Hiding the screener was here, as a tick, while the toolbar
                // slot could not express it. It has moved to an × on the
                // screener's own group bar: a panel is hidden from the panel,
                // and a control reachable only through a right-click on a chart
                // is one nobody finds. The strip's third slot brings it back.
                ...(group
                  ? GRID_LINE_CHOICES.map((choice) => ({
                      id: `gridlines-${choice.id}`,
                      // A tick beside the active one, because these four are a
                      // single setting rather than four commands.
                      label: `${(group.gridLines ?? "solid") === choice.id ? "✓ " : " "}Gridlines: ${choice.label}`,
                      onSelect: () =>
                        dispatch({
                          type: "setGridLines",
                          chartGroupId: group.id,
                          gridLines: choice.id,
                        }),
                    }))
                  : []),
                ...(group
                  ? RIGHT_MARGIN_CHOICES.map((choice) => ({
                      id: `right-margin-${choice.id}`,
                      // Beside Gridlines because it is the same kind of thing: a
                      // property of this chart's axes that a person picks from a
                      // short list, and workspace data rather than a theme token
                      // for the reason every setting in this menu is.
                      label: `${(group.rightMargin ?? 0) === choice.id ? "✓ " : " "}Right margin: ${choice.label}`,
                      onSelect: () =>
                        dispatch({
                          type: "setRightMargin",
                          chartGroupId: group.id,
                          rightMargin: choice.id,
                        }),
                    }))
                  : []),
                ...(group
                  ? PRICE_FIT_CHOICES.map((choice) => ({
                      id: `price-fit-${choice.id}`,
                      // Beside the other two for their reason: a property of
                      // this chart's axes, picked from a short list. What it
                      // fixes is an overlay that runs away from the bars — a
                      // long average after a gap, a band drawn wide — which the
                      // axis has to cover, compressing the candles into a strip
                      // in the middle of the pane.
                      // Ticked only while the axis follows the fit: a hand-scaled
                      // axis is *Manual*, whatever the fit would read.
                      label: `${!isYManual(group.id) && (group.priceFit ?? "price") === choice.id ? "✓ " : " "}Price axis: ${choice.label}`,
                      onSelect: () => {
                        dispatch({
                          type: "setPriceFit",
                          chartGroupId: group.id,
                          priceFit: choice.id,
                        });
                        // Back to auto even when the choice is already the setting.
                        requestYAxisMode(group.id, "auto");
                      },
                    }))
                  : []),
                ...(group
                  ? [
                      {
                        id: "price-fit-manual",
                        // Ticked by a drag on the axis; picked, it freezes the range
                        // on screen. Not saved — ›| or an auto choice ends it
                        // (`docs/features/charts.md` › Axes and ranging).
                        label: `${isYManual(group.id) ? "✓ " : " "}Price axis: Manual`,
                        onSelect: () => requestYAxisMode(group.id, "manual"),
                      },
                    ]
                  : []),
                ...(group
                  ? [
                      {
                        id: "axis-drag",
                        // One item that toggles, not two that set: it is a
                        // boolean, and "Axis drag: off" beside "Axis drag: on"
                        // would read as two independent commands.
                        label: `${(group.axisDrag ?? true) ? "✓ " : " "}Drag axis to scale`,
                        onSelect: () =>
                          dispatch({
                            type: "setAxisDrag",
                            chartGroupId: group.id,
                            axisDrag: !(group.axisDrag ?? true),
                          }),
                      },
                      {
                        id: "volume-overlay",
                        // "on price" rather than "overlay", which names the
                        // mechanism. What the user is choosing is where the bars
                        // sit, and the answer is under the candles or beside
                        // them.
                        label: `${volumeOverlaid(group) ? "✓ " : " "}Volume on price`,
                        onSelect: () =>
                          dispatch({
                            type: "setVolumeOverlay",
                            groupId: group.id,
                            overlay: !volumeOverlaid(group),
                          }),
                      },
                      {
                        id: "show-earnings",
                        // Named for what appears on the chart rather than for
                        // the data behind it: the user is choosing whether an
                        // `E` sits under the candles, not whether a fundamentals
                        // document is fetched.
                        label: `${eventsFor(group.id).earnings ? "✓ " : " "}Earnings markers`,
                        onSelect: () =>
                          dispatch({
                            type: "setChartEvents",
                            chartGroupId: group.id,
                            events: { earnings: !eventsFor(group.id).earnings },
                          }),
                      },
                      {
                        id: "show-news",
                        label: `${eventsFor(group.id).news ? "✓ " : " "}News marker`,
                        onSelect: () =>
                          dispatch({
                            type: "setChartEvents",
                            chartGroupId: group.id,
                            events: { news: !eventsFor(group.id).news },
                          }),
                      },
                      {
                        id: "value-legend",
                        // Not "Legend", which reads as a key of colours — the
                        // one thing this is not. Short enough to sit in a menu
                        // of four-word items without being the long one.
                        label: `${(group.valueLegend ?? true) ? "✓ " : " "}Show Values`,
                        onSelect: () =>
                          dispatch({
                            type: "setValueLegend",
                            chartGroupId: group.id,
                            valueLegend: !(group.valueLegend ?? true),
                          }),
                      },
                      {
                        id: "project-forward",
                        // Named for what it does to the *indicators*, not for
                        // seasonality: the seasonal path is what supplies the
                        // closes, but what changes on screen is every other
                        // line growing a tail.
                        label: `${group.projectForward ? "✓ " : " "}Project forward`,
                        onSelect: () =>
                          dispatch({
                            type: "setProjectForward",
                            chartGroupId: group.id,
                            projectForward: !group.projectForward,
                          }),
                      },
                      ...ZOOM_AXIS_CHOICES.map((choice) => ({
                        id: `zoom-${choice.id}`,
                        label: `${(group.zoomAxis ?? "x") === choice.id ? "✓ " : " "}Zoom / pan: ${choice.label}`,
                        onSelect: () =>
                          dispatch({
                            type: "setZoomAxis",
                            chartGroupId: group.id,
                            zoomAxis: choice.id,
                          }),
                      })),
                    ]
                  : []),
              ],
            },
          ]}
        />
      )}

      {templateFor && (() => {
        // Resolved here rather than held in state — see `templateFor`. A chart
        // deleted between the right-click and the dialog opening leaves nothing
        // to act on, and a dialog about a chart that is gone is worse than none.
        const target = layout?.sections
          .map((sec) => chartGroupConfig(sec))
          .find((g) => g?.id === templateFor.groupId);
        if (!target) return null;
        const charts = (layout?.sections ?? [])
          .map((sec) => chartGroupConfig(sec))
          .filter(Boolean).length;
        return templateFor.mode === "save" ? (
          <SaveChartTemplate
            group={target}
            publishTarget={publishTarget}
            publishTargetName={publishTargetName}
            onSaved={() => setTemplateFor(null)}
            onClose={() => setTemplateFor(null)}
          />
        ) : (
          <ApplyChartTemplate
            group={target}
            symbolLinked={layout?.sync?.symbol === true}
            chartsInLayout={charts}
            {...(publishTarget
              ? { authoringFor: { group: publishTarget, name: publishTargetName ?? publishTarget } }
              : {})}
            // One dispatch: one undo, one save, and the symbol fans out from
            // inside the reducer if it moved — see `replaceChartGroup`.
            onApply={(next) =>
              dispatch({ type: "replaceChartGroup", chartGroupId: target.id, group: next })
            }
            onClose={() => setTemplateFor(null)}
          />
        );
      })()}

      {themeEditorOpen && (
        <ThemeEditor
          activeThemeRef={workspace.themeRef}
          // Straight into the workspace: the selected theme is workspace data,
          // and the repaint follows from `themeRef` changing — no separate
          // "apply" step, and nothing else to keep in sync.
          onSelect={(themeRef) => dispatch({ type: "setTheme", themeRef })}
          themes={themes}
          onSaveTheme={onSaveTheme}
          onClose={() => setThemeEditorOpen(false)}
        />
      )}

      {/* Clubs, plans and grants. A `DraggableDialog` rather than a modal for
          the reason the plan form is one: an admin checking what a club grants
          usually wants to look at the screen it affects while they do it. */}
      {/* Community with no dock to stand in: the data panel's fallback. */}
      {communityFloating && !layoutHasDock && communityPanel && (
        <DraggableDialog title="Community" onClose={() => setCommunityFloating(false)}>
          <div style={{ width: 460, height: 560, display: "flex", flexDirection: "column" }}>{communityPanel}</div>
        </DraggableDialog>
      )}
      {/* The AI panel with no dock to stand in: Community's fallback. */}
      {aiFloating && !layoutHasDock && aiPanel && (
        <DraggableDialog title="AI" onClose={() => setAiFloating(false)}>
          <div style={{ width: 440, height: 560, display: "flex", flexDirection: "column" }}>{aiPanel}</div>
        </DraggableDialog>
      )}

      {/* Train: one case for QueVee Voice from the chart it was opened on. */}
      {training && voices.length > 0 && (
        <TrainDialog
          key={`${training.chartGroupId}|${training.symbol}|${training.frequency}`}
          chartGroupId={training.chartGroupId}
          symbol={training.symbol}
          frequency={training.frequency}
          voices={voices}
          onClose={() => setTraining(null)}
        />
      )}

      {adminOpen && (
        <DraggableDialog title="Clubs & access" onClose={() => setAdminOpen(false)}>
          <AdminPanel />
        </DraggableDialog>
      )}

      {/* The fleet's DQHealth. A dialog rather than a route for `AdminPanel`'s
          reason: somebody checking whether the data moved usually wants the
          screen it feeds open beside it. */}
      {dataHealthOpen && (
        <DraggableDialog title="Data health" onClose={() => setDataHealthOpen(false)}>
          <DataHealthPanel />
        </DraggableDialog>
      )}

      {/* Read-only, and offered to everyone — it is what answers "why can I not
          see that", and the person asking is the one without the capability. */}
      {/* Books and broker imports. A dialog for `AdminPanel`'s reason, and so an
          upload in progress survives a glance at the chart behind it. */}
      {booksOpen && (
        <DraggableDialog title="Books" onClose={() => setBooksOpen(false)}>
          <BooksPanel />
        </DraggableDialog>
      )}

      {planOpen && (
        <DraggableDialog title="Your plan" onClose={() => setPlanOpen(false)}>
          <PlanScreen billingReturn={billingReturn} intent={planIntent} />
        </DraggableDialog>
      )}

      {groupRequestOpen && (
        <DraggableDialog title="Request a group" onClose={() => setGroupRequestOpen(false)}>
          <GroupRequestForm />
        </DraggableDialog>
      )}

      {/*
        The plan form, over the layout rather than inside a pane: it is about a
        symbol rather than about one chart's geometry, and putting it in the
        grid would make writing a plan resize the chart it is about.

        Held until the account has arrived, because a form with nothing to size
        against would produce a save the server refuses — and the arithmetic
        down the right-hand side is the whole point of the form.
      */}
      {builder && screener && screenerSection && (() => {
        const current = screens.find((sc) => sc.key === screener.screenRef);
        // New starts where a blank screen does — the default columns, the
        // library carried and nothing ticked; Edit starts from the screen.
        const seed =
          builder === "new"
            ? {
                ...screener,
                columns: [...DEFAULT_COLUMNS],
                savedFilters: libraryForAnotherScreen(storedScreener?.savedFilters ?? []),
                activeFilterRef: undefined,
                activeFilterRefs: [],
                sort: [],
              }
            : screener;
        return (
          <ScreenBuilder
            mode={builder}
            config={seed}
            universe={buildColumnUniverse({
              fields: screenerSchema.fields,
              meta: screenerSchema.meta,
              columns: seed.columns,
            })}
            name={builder === "new" ? nextFreeName(screens) : (current?.name ?? "My screen")}
            universeLabel={screener.group || undefined}
            describe={builderDescribeEnabled() ? describeInBuilder : undefined}
            describeColumns={screenerSchema.meta.map((m) => ({
              field: m.field,
              label: m.label,
              description: m.description,
            }))}
            mintId={mintUlid}
            // Movers presets — screens and conditions — need their
            // `screen:preset:*` grant; without it they are hidden (movers.md).
            presetGate={holds}
            takenNames={screens.map((s) => s.name)}
            onClose={() => setBuilder(null)}
            onFullEditor={() => {
              setBuilder(null);
              setEditorTab("columns");
              setEditorOpen(true);
            }}
            onDone={(built) => {
              setBuilder(null);
              if (builder === "new") {
                createScreen(built);
                return;
              }
              dispatch({ type: "updateScreener", sectionId: screenerSection.id, patch: built.patch });
              // Only a screen of your own carries a name you can change.
              if (current && current.ownerKind === "user" && built.name !== current.name) {
                void renameScreen(current.key, built.name).catch(() => {});
              }
            }}
          />
        );
      })()}
      {/* One symbol search for the whole app. The chart it answers for was
          recorded when it opened — see `search`. */}
      {search !== null && (
        <SymbolDialog
          initialQuery={search.query}
          selectSeed={search.select}
          keeps={search.keeps}
          onPick={(symbol) => {
            // `symbol` is in `ChartGroupView`'s `structuralKey`, so this one
            // dispatch *is* the refetch: the surface is torn down and rebuilt
            // against the new symbol. Which is why nothing commits per
            // keystroke — typing `AAPL` would have cost four rebuilds.
            //
            // Locked while the dialog was open: the reducer would refuse it
            // silently, so the toast says why nothing moved.
            if (routeTyped(workspace, search.chartGroupId).kind === "locked") {
              refuseLocked(search.chartGroupId);
            } else {
              dispatch({ type: "setSymbol", chartGroupId: search.chartGroupId, symbol });
            }
            setSearch(null);
          }}
          onClose={() => setSearch(null)}
        />
      )}

      {planningSymbol !== null && planAccountId !== null && (
        // **No scrim.** The chart is what you are writing the plan against, so
        // dimming it — and closing the form on any click outside — was fighting
        // the reason the form is open at all. It moves out of the way instead.
        <DraggableDialog
          title={editingPlan ? `Edit plan · ${planningSymbol}` : `New plan · ${planningSymbol}`}
          onClose={() => {
            setPlanningSymbol(null);
            setEditingPlan(null);
          }}
        >
          <div style={{ overflowY: "auto" }}>
            <PlanForm
              symbol={planningSymbol}
              accountId={planAccountId}
              plan={editingPlan ?? undefined}
              // Seeds a *new* plan only — `PlanForm` ignores it when editing,
              // or opening a saved plan would overwrite its entry with today's
              // price.
              price={planningSymbol ? prices.get(planningSymbol.toUpperCase()) : undefined}
              // Where a typed plan's levels start. Ignored when editing, for
              // `price`'s reason: reopening a saved plan must not move it.
              startAt={planningSymbol ? barTimes.get(planningSymbol.toUpperCase()) : undefined}
              onSaved={() => {
                setPlanningSymbol(null);
                setEditingPlan(null);
                // Re-reads the panel and re-derives the chart's lines, which is
                // the feedback that was missing entirely: arming a plan used to
                // close the dialog and change nothing on screen.
                setJournalStamp((n) => n + 1);
              }}
              onCancel={() => {
                setPlanningSymbol(null);
                setEditingPlan(null);
              }}
            />
          </div>
        </DraggableDialog>
      )}
    </ViewDetachContext.Provider>
  );
}

/**
 * One half of the vertical strip.
 *
 * `flex: 1 1 0` on both halves is what makes the split 50/50 regardless of how
 * tall the chart is — a fixed height would leave the drawing tools floating in
 * the middle of a tall layout and clipped on a short one.
 *
 * `minHeight: 0` because a flex child otherwise refuses to shrink below its
 * content, which would push the seam off the bottom of a short chart and take
 * the second half with it. `overflowY: auto` is the other half of that bargain:
 * when there genuinely is not room, a half scrolls rather than hiding buttons
 * that have no other way to be reached.
 */
const STRIP_HALF: React.CSSProperties = {
  // `auto` rather than `0`, and **no `minHeight: 0`** — the two together are
  // what makes this safe.
  //
  // `flex: 1 1 0` with `minHeight: 0` gives each half exactly half the strip
  // whatever it holds, and a half too short for its buttons then hides them
  // behind a scrollbar 34px wide that nobody finds. That is what happened to
  // Saved filters: five controls in the top half, half the height, and the last
  // one scrolled out of sight on any chart that was not tall.
  //
  // `1 1 auto` still shares the spare room equally, so it reads as 50/50
  // whenever there is room — but the automatic minimum is the content, so
  // neither half can shrink below its own buttons. When the strip genuinely
  // cannot fit both, the strip scrolls as one thing, which is at least
  // discoverable.
  flex: "1 1 auto",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.3rem",
};

/** The rule between the halves. Inset, so it reads as a divider and not a border. */
const STRIP_SEAM: React.CSSProperties = {
  flex: "0 0 auto",
  width: "60%",
  height: 1,
  margin: "0.15rem 0",
  background: "var(--qf-border)",
};

/**
 * The editor column: the screen tabs, then the builder filling what is left.
 *
 * `minHeight: 0` on the inner half, or the builder's own scroll region grows the
 * column instead of scrolling inside it — the flexbox default that makes a
 * nested scroller silently stop scrolling.
 */
const EDITOR_COLUMN: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
};
